import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import SaveButton from '@/components/SaveButton';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin, Calendar, Users, MessageCircle, Trash2, Edit, Flag, Camera, Image, X, Share2, Bell, EyeOff, Ticket, Upload, RefreshCw, ChevronLeft, ChevronRight, Megaphone, Send, Download, Pin, Paperclip, FileText, UserPlus, BookmarkPlus } from 'lucide-react';
import { format } from 'date-fns';
import AuthDialog from '@/components/AuthDialog';
import MembershipModal from '@/components/MembershipModal';
import ReportDialog from '@/components/ReportDialog';
// EventRecapCard import removed per host feedback — card no longer rendered.
import { buildAlbumCollage, downloadBlob } from '@/lib/albumCollage';
import { calcHeadcount } from '@/lib/calcHeadcount';
import usePushNotifications from '@/hooks/usePushNotifications';
import { useIsIOSApp } from '@/hooks/useIsIOSApp';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const Linkify = ({ text }) => {
  // Match http/https URLs AND file:// URLs. The latter can only work on the
  // author's own machine — we render a warning instead of a live link so
  // recipients aren't left tapping something that will silently fail.
  const urlRegex = /(https?:\/\/[^\s]+|file:\/\/\/?[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (/^https?:\/\//.test(part)) {
      const cleaned = part.replace(/[.,;:!?)]+$/, '');
      const trailing = part.slice(cleaned.length);
      return (
        <span key={i}>
          <a href={cleaned} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80 break-all">{cleaned}</a>
          {trailing}
        </span>
      );
    }
    if (/^file:\/\//.test(part)) {
      return (
        <span
          key={i}
          className="inline-flex flex-wrap items-baseline gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs align-baseline"
          title="This path points to a file on the poster's own computer — nobody else can open it."
          data-testid={`local-file-link-warning-${i}`}
        >
          <span className="font-mono break-all">{part}</span>
          <span className="text-[10px] italic">← this file lives on the poster's computer, ask them to attach it here</span>
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  // Number of +1 guests the current member will bring (pre-RSVP stepper value)
  const [pendingGuestsCount, setPendingGuestsCount] = useState(0);
  const [pendingGuestNotes, setPendingGuestNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [updatingGuests, setUpdatingGuests] = useState(false);
  // Host popover for adjusting an attendee's guest count
  const [hostGuestEditor, setHostGuestEditor] = useState(null); // {user_id, name, current}
  const [savingHostGuest, setSavingHostGuest] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [showMembership, setShowMembership] = useState(false);
  const isIOSApp = useIsIOSApp();
  const [showReport, setShowReport] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoCaption, setPhotoCaption] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [showAttendees, setShowAttendees] = useState(false);
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [attendanceRequests, setAttendanceRequests] = useState([]);
  const [hasRequestedAttendance, setHasRequestedAttendance] = useState(false);
  const photoInputRef = useRef(null);
  
  // Edit image state
  const [showEditImage, setShowEditImage] = useState(false);
  const [newImageFile, setNewImageFile] = useState(null);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [savingImage, setSavingImage] = useState(false);
  const editImageInputRef = useRef(null);
  
  // Push notifications
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, subscribe: subscribeToPush } = usePushNotifications();

  // Comment edit/delete state
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  // Attachment state — arrays of {filename, url, mime_type, size_bytes}
  // captured from the upload endpoint before the comment is posted/saved.
  const [newAttachments, setNewAttachments] = useState([]);
  const [editAttachments, setEditAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const attachmentInputRef = useRef(null);
  const editAttachmentInputRef = useRef(null);
  
  // Description edit state
  const [editingDescription, setEditingDescription] = useState(false);
  const [editDescriptionText, setEditDescriptionText] = useState('');
  
  // Reminder state
  const [sendingReminder, setSendingReminder] = useState(false);
  
  // Promote state
  const [promoting, setPromoting] = useState(false);
  const [showPromoteDialog, setShowPromoteDialog] = useState(false);
  const [promoteNote, setPromoteNote] = useState('');
  const [promoteScope, setPromoteScope] = useState('city'); // 'city' | 'group'
  const [promoteGroupSlugs, setPromoteGroupSlugs] = useState([]);
  const [promotionHistory, setPromotionHistory] = useState([]);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  // Attendee-only nudge: separate rate limit, host-only, no public cooldown
  const [showNudgeDialog, setShowNudgeDialog] = useState(false);
  const [nudgeNote, setNudgeNote] = useState('');
  const [nudgeIncludeWaitlist, setNudgeIncludeWaitlist] = useState(true);
  const [nudging, setNudging] = useState(false);

  // Download to Share: let users cherry-pick which photos go into the collage
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  // Rescue (delete + rebook) flow — opens on "Delete Event" click. Gives
  // the host a one-tap "Suggest a new date" alternative to a plain cancel.
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [rebookDate, setRebookDate] = useState('');
  const [rebookTime, setRebookTime] = useState('');
  const [rebookNote, setRebookNote] = useState('');
  const [rebookBusy, setRebookBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [selectedDownloadIds, setSelectedDownloadIds] = useState([]);
  const [buildingCollage, setBuildingCollage] = useState(false);
  const [downloadCaption, setDownloadCaption] = useState('');
  const [captionStyle, setCaptionStyle] = useState('quote'); // 'quote' | 'handwritten' | 'headline'
  const [includeHeader, setIncludeHeader] = useState(true);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [draggingPhotoId, setDraggingPhotoId] = useState(null);
  const previewReqIdRef = useRef(0);

  useEffect(() => {
    // Fetch event, comments, and photos in parallel for faster loading
    Promise.all([
      axios.get(`${API}/events/${eventId}`).then(r => { setEvent(r.data); }),
      axios.get(`${API}/events/${eventId}/comments`).then(r => { setComments(r.data); }).catch(() => {}),
      axios.get(`${API}/events/${eventId}/photos`).then(r => { setPhotos(r.data.photos || r.data || []); }).catch(() => {})
    ]).catch(error => {
      console.error('Failed to fetch event:', error);
      toast.error('Event not found');
      navigate('/events');
    }).finally(() => {
      setLoading(false);
    });
  }, [eventId]);

  useEffect(() => {
    if (user && event) {
      fetchAttendees();
      if (event.is_invite_only) {
        setHasRequestedAttendance(event.attendance_requests?.includes(user.user_id) || false);
        if (event.creator_id === user.user_id) {
          fetchAttendanceRequests();
        }
      }
      // Host: fetch promotion analytics once per event load
      if (event.creator_id === user.user_id) {
        fetchPromotionHistory();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, event]);

  // Recap-photo deep-link: `?compose=photo` (fired from the day-after-event
  // notification / email CTA) auto-opens the upload form and scrolls to the
  // photos section so the user can drop pictures in one tap.
  const composeOpenedRef = useRef(false);
  useEffect(() => {
    if (composeOpenedRef.current || !event || !user) return;
    const params = new URLSearchParams(location.search);
    if (params.get('compose') !== 'photo') return;
    // Inline the canUploadPhotos check — the memoised version lives after
    // the early-return guards below, so we can't reference it in a hook.
    const eventDate = (event.event_date || '').slice(0, 10);
    const today = new Date().toISOString().split('T')[0];
    const isPast = Boolean(eventDate) && eventDate < today;
    const isCreatorLocal = user.user_id === event.creator_id || user.user_id === event.organizer_id;
    const isAttendingLocal = (event.attendees || []).includes(user.user_id);
    const canUpload = isCreatorLocal || isAttendingLocal || user.is_admin || isPast;
    if (!canUpload) return;
    composeOpenedRef.current = true;
    setShowPhotoUpload(true);
    // Defer scroll until the upload form has mounted.
    setTimeout(() => {
      const el = document.getElementById('photos') || document.getElementById('photos-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
  }, [event, user, location.search]);

  // Scroll to comment/section if hash fragment is present
  const scrolledRef = useRef(false);
  const scrollCleanupRef = useRef(null);
  
  useEffect(() => {
    scrolledRef.current = false;
    if (scrollCleanupRef.current) {
      scrollCleanupRef.current();
      scrollCleanupRef.current = null;
    }
  }, [location.pathname, location.hash]);
  
  useEffect(() => {
    if (scrolledRef.current || !location.hash) return;
    const hash = location.hash.substring(1);
    if (!hash) return;

    let targetId = hash;
    if (hash === 'comments') targetId = 'comments-section';

    const el = document.getElementById(targetId);
    if (!el) return; // Will retry on next render when element appears

    scrolledRef.current = true;

    // Highlight specific comments
    if (hash.startsWith('comment-')) {
      el.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
      setTimeout(() => el.classList.remove('ring-2', 'ring-primary', 'ring-offset-2'), 3000);
    }

    // Position-tracking scroll that adapts to layout shifts from async image loading.
    // Instead of fixed timeouts, we poll the element's position and re-scroll
    // whenever layout shifts move it away. Stops when stable or user interacts.
    const NAVBAR_OFFSET = 96;
    const MAX_DURATION = 12000;
    const POLL_INTERVAL = 400;
    let stableCount = 0;
    let userScrolled = false;
    let lastKnownTop = null;

    const onUserInteraction = () => { userScrolled = true; };

    // Delay adding listener so our own scrolls don't trigger it
    const listenerDelay = setTimeout(() => {
      window.addEventListener('wheel', onUserInteraction, { once: true, passive: true });
      window.addEventListener('touchstart', onUserInteraction, { once: true, passive: true });
    }, 800);

    const scrollToTarget = () => {
      const target = document.getElementById(targetId);
      if (!target) return;
      const top = target.getBoundingClientRect().top + window.pageYOffset - NAVBAR_OFFSET;
      window.scrollTo({ top, behavior: 'smooth' });
    };

    // Initial scroll
    scrollToTarget();

    const interval = setInterval(() => {
      if (userScrolled) { clearInterval(interval); return; }
      const target = document.getElementById(targetId);
      if (!target) return;

      const rect = target.getBoundingClientRect();
      const currentTop = rect.top;

      // Position is stable if it hasn't changed between polls
      const positionStable = lastKnownTop !== null && Math.abs(currentTop - lastKnownTop) < 5;

      if (positionStable) {
        stableCount++;
        if (stableCount >= 3) {
          clearInterval(interval);
          if (hash === 'add-comment') {
            const textarea = target.querySelector('textarea');
            if (textarea) textarea.focus();
          }
          return;
        }
      } else {
        // Layout shifted — re-scroll to correct position
        stableCount = 0;
        scrollToTarget();
      }
      lastKnownTop = currentTop;
    }, POLL_INTERVAL);

    // Hard stop after MAX_DURATION
    const timeout = setTimeout(() => {
      clearInterval(interval);
      // One final scroll and focus
      scrollToTarget();
      if (hash === 'add-comment') {
        const target = document.getElementById(targetId);
        if (target) {
          const textarea = target.querySelector('textarea');
          if (textarea) textarea.focus();
        }
      }
    }, MAX_DURATION);

    scrollCleanupRef.current = () => {
      clearInterval(interval);
      clearTimeout(timeout);
      clearTimeout(listenerDelay);
      window.removeEventListener('wheel', onUserInteraction);
      window.removeEventListener('touchstart', onUserInteraction);
    };
  });

  // Live-regenerate the collage preview whenever the user adjusts the
  // selection or the caption in the Download-to-Share dialog. Debounced so
  // typing a caption doesn't rebuild on every keystroke, and tagged with a
  // request id so a slow build from an older state can't overwrite the newer
  // preview.
  useEffect(() => {
    if (!showDownloadDialog) {
      setPreviewUrl('');
      setPreviewLoading(false);
      return undefined;
    }
    if (!event || selectedDownloadIds.length === 0) {
      setPreviewUrl('');
      setPreviewLoading(false);
      return undefined;
    }
    const myId = ++previewReqIdRef.current;
    setPreviewLoading(true);
    const timer = setTimeout(async () => {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const idSet = new Set(selectedDownloadIds);
      const items = photos
        .filter((p) => idSet.has(p.photo_id))
        .map((p) => {
          let src = p.image_url || p.image_data;
          if (src && src.startsWith('/api/')) src = `${backend}${src}`;
          return { url: src, caption: p.caption };
        })
        .filter((p) => p.url);
      if (items.length === 0) {
        if (previewReqIdRef.current === myId) {
          setPreviewUrl('');
          setPreviewLoading(false);
        }
        return;
      }
      try {
        const dateLabel = event.event_date
          ? new Date(event.event_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
          : undefined;
        const { dataUrl } = await buildAlbumCollage(items, {
          title: event.title,
          attendeeCount: calcHeadcount(event),
          dateLabel,
          caption: downloadCaption,
          captionStyle,
          includeHeader,
        });
        if (previewReqIdRef.current === myId) {
          setPreviewUrl(dataUrl);
          setPreviewLoading(false);
        }
      } catch (_) {
        if (previewReqIdRef.current === myId) {
          setPreviewLoading(false);
        }
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [showDownloadDialog, selectedDownloadIds, downloadCaption, captionStyle, includeHeader, photos, event]);

  const fetchAttendanceRequests = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/attendance-requests`);
      setAttendanceRequests(response.data.requests || []);
    } catch (error) {
      console.error('Failed to fetch attendance requests');
    }
  };

  const [waitlistUsers, setWaitlistUsers] = useState([]);
  const fetchAttendees = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/attendees`);
      setAttendees(response.data.attendees || []);
      setIsOrganizer(response.data.is_organizer || false);
      setWaitlistUsers(response.data.waitlist || []);
    } catch (error) {
      console.error('Failed to fetch attendees');
    }
  };

  const markRsvpsViewed = async () => {
    try {
      await axios.post(`${API}/events/${eventId}/mark-rsvps-viewed`);
    } catch (error) {
      console.error('Failed to mark RSVPs as viewed');
    }
  };

  const handleShowAttendees = () => {
    setShowAttendees(!showAttendees);
    if (!showAttendees && isOrganizer) {
      // Mark RSVPs as viewed when organizer opens the list
      markRsvpsViewed();
    }
  };

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/photos`);
      setPhotos(response.data);
    } catch (error) {
      console.error('Failed to fetch photos');
    }
  };

  const compressImage = (file, maxWidth = 1920, quality = 0.85) => {
    return new Promise((resolve) => {
      const img = new window.Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        let { width, height } = img;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
      };
      img.src = url;
    });
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    if (files.length > 10) {
      toast.error('Max 10 photos at a time');
      return;
    }

    setUploadingPhoto(true);
    let uploaded = 0;
    let failed = 0;

    for (const file of files) {
      try {
        // Compress on client side (resize to max 1920px width, JPEG 85% quality)
        const compressed = await compressImage(file);
        const formData = new FormData();
        formData.append('file', compressed, file.name || 'photo.jpg');
        formData.append('caption', photoCaption || '');

        await axios.post(`${API}/events/${eventId}/photos/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploaded++;
      } catch (error) {
        failed++;
        console.error('Upload failed:', file.name, error);
      }
    }

    if (uploaded > 0) {
      toast.success(`${uploaded} photo${uploaded > 1 ? 's' : ''} uploaded!`);
      setPhotoCaption('');
      setShowPhotoUpload(false);
      fetchPhotos();
    }
    if (failed > 0) {
      toast.error(`${failed} photo${failed > 1 ? 's' : ''} failed to upload`);
    }
    setUploadingPhoto(false);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [deletingPhoto, setDeletingPhoto] = useState(false);

  // Attendee report-a-photo flow
  const [photoToReport, setPhotoToReport] = useState(null);
  const [reportReason, setReportReason] = useState('inappropriate');
  const [reportNote, setReportNote] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  // Host-side: list of reports for the currently-open lightbox photo
  const [currentPhotoReports, setCurrentPhotoReports] = useState([]);

  const handleDeletePhoto = (photo) => {
    // Open our in-app confirm dialog instead of the native `confirm()` so
    // the message can show the uploader name — important when the host is
    // moderating someone else's photo.
    setPhotoToDelete(photo);
  };

  const confirmDeletePhoto = async () => {
    if (!photoToDelete) return;
    setDeletingPhoto(true);
    try {
      await axios.delete(`${API}/events/${eventId}/photos/${photoToDelete.photo_id}`);
      toast.success('Photo deleted');
      fetchPhotos();
      setSelectedPhoto(null);
      setPhotoToDelete(null);
    } catch (error) {
      toast.error('Failed to delete photo');
    } finally {
      setDeletingPhoto(false);
    }
  };

  const openReportDialog = (photo) => {
    setReportReason('inappropriate');
    setReportNote('');
    setPhotoToReport(photo);
  };

  const submitPhotoReport = async () => {
    if (!photoToReport) return;
    setReportBusy(true);
    try {
      const res = await axios.post(
        `${API}/events/${eventId}/photos/${photoToReport.photo_id}/report`,
        { reason: reportReason, note: reportNote.trim() || undefined },
      );
      toast.success(res.data.already_reported
        ? 'Already reported. The host will review it.'
        : 'Reported. The host will review it.');
      setPhotoToReport(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report photo');
    } finally {
      setReportBusy(false);
    }
  };

  // When the host opens a photo that's been reported, fetch the reports so
  // we can render the yellow review banner. `isCreator` is computed inline
  // because the memoised version lives after the early-return guards below.
  const isCreatorForReports = Boolean(
    user && event && (user.user_id === event.creator_id || user.user_id === event.organizer_id)
  );
  useEffect(() => {
    if (!selectedPhoto || !isCreatorForReports) {
      setCurrentPhotoReports([]);
      return;
    }
    if ((selectedPhoto.report_count || 0) === 0) {
      setCurrentPhotoReports([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/events/${eventId}/photos/${selectedPhoto.photo_id}/reports`,
        );
        if (!cancelled) setCurrentPhotoReports(res.data?.reports || []);
      } catch (_) {
        if (!cancelled) setCurrentPhotoReports([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPhoto, isCreatorForReports, eventId]);

  // Deep-link: /events/{id}?review_photo={photo_id} auto-opens the reported
  // photo for the host (notification click lands here).
  useEffect(() => {
    const reviewId = new URLSearchParams(location.search).get('review_photo');
    if (!reviewId || !photos || photos.length === 0) return;
    const target = photos.find((p) => p.photo_id === reviewId);
    if (target) setSelectedPhoto(target);
  }, [location.search, photos]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`);
      setEvent(response.data);
    } catch (error) {
      console.error('Failed to fetch event:', error);
      toast.error('Event not found');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const handleRSVP = async (opts = {}) => {
    if (!user) {
      setShowAuth(true);
      return;
    }

    const joinWaitlist = !!opts.joinWaitlist;
    try {
      const guests = event?.allow_guests ? Math.max(0, parseInt(pendingGuestsCount) || 0) : 0;
      const notes = event?.allow_guests ? (pendingGuestNotes || '').trim() : '';
      const { data } = await axios.post(`${API}/events/${eventId}/rsvp`, {
        guests_count: guests,
        guest_notes: notes || null,
        join_waitlist: joinWaitlist,
      });
      if (data && data.waitlisted) {
        toast.success(`Added to the waitlist (position #${data.waitlist_position}). We'll email you if a spot opens.`);
      } else {
        toast.success(guests > 0 ? `RSVP confirmed with ${guests} guest${guests !== 1 ? 's' : ''}!` : 'RSVP confirmed!');
      }
      setPendingGuestsCount(0);
      setPendingGuestNotes('');
      fetchEvent();

      // Ask for push notification permission after successful RSVP
      if (!joinWaitlist && pushSupported && !pushSubscribed) {
        const subscribed = await subscribeToPush();
        if (subscribed) {
          toast.success('You\'ll get a reminder before the event!', { icon: '🔔' });
        }
      }
    } catch (error) {
      if (error.response?.status === 403) {
        if (isIOSApp) {
          toast.info('Membership required', {
            description: 'Visit expatssphere.com in your browser to upgrade and RSVP to this event.',
            duration: 8000,
          });
        } else {
          setShowMembership(true);
        }
      } else {
        toast.error(error.response?.data?.detail || 'Failed to RSVP');
      }
    }
  };

  const handleJoinWaitlist = () => handleRSVP({ joinWaitlist: true });

  const handleLeaveWaitlist = async () => {
    try {
      await axios.delete(`${API}/events/${eventId}/waitlist`);
      toast.success('Left the waitlist');
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave waitlist');
    }
  };

  const handleUpdateGuestCount = async (nextCount, opts = {}) => {
    if (!event) return;
    const max = event.max_guests_per_rsvp ?? 99;
    const clamped = Math.max(0, Math.min(max, nextCount));
    setUpdatingGuests(true);
    try {
      const payload = { guests_count: clamped };
      if (opts.notes !== undefined) payload.guest_notes = opts.notes;
      await axios.patch(`${API}/events/${eventId}/rsvp/guests`, payload);
      toast.success(clamped > 0 ? `Bringing ${clamped} guest${clamped !== 1 ? 's' : ''}` : 'Guest count cleared');
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update guest count');
    } finally {
      setUpdatingGuests(false);
    }
  };

  const handleSaveMyNotes = async (notes) => {
    if (!event) return;
    setSavingNotes(true);
    try {
      const currentGuests = (event.guest_counts && event.guest_counts[user.user_id]) || 0;
      await axios.patch(`${API}/events/${eventId}/rsvp/guests`, {
        guests_count: currentGuests,
        guest_notes: (notes || '').trim(),
      });
      toast.success('Notes saved');
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleHostSaveAttendeeGuests = async (attendeeUserId, guestsCount) => {
    setSavingHostGuest(true);
    try {
      await axios.patch(`${API}/events/${eventId}/attendees/${attendeeUserId}/guests`, {
        guests_count: guestsCount,
      });
      toast.success('Attendee guest count updated');
      setHostGuestEditor(null);
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update guests');
    } finally {
      setSavingHostGuest(false);
    }
  };

  const handleEnableGuestsForEvent = async () => {
    try {
      await axios.patch(`${API}/events/${eventId}/enable-guests`, {});
      toast.success('+1 Guests enabled for this event');
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enable guests');
    }
  };

  const handleSaveAsTemplate = async () => {
    try {
      const { data } = await axios.post(`${API}/event-templates/from-event/${eventId}`);
      toast.success(`Saved "${data.name}" as a template`, {
        description: 'Use it next time you create an event.',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleCancelRSVP = async () => {
    try {
      await axios.delete(`${API}/events/${eventId}/rsvp`);
      toast.success('RSVP cancelled');
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel RSVP');
    }
  };

  const handleRequestAttendance = async () => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    try {
      await axios.post(`${API}/events/${eventId}/request-attendance`);
      toast.success('Request sent! The organizer will review it.');
      setHasRequestedAttendance(true);
    } catch (error) {
      if (error.response?.status === 403) {
        if (isIOSApp) {
          toast.info('Membership required', {
            description: 'Visit expatssphere.com in your browser to upgrade and request attendance.',
            duration: 8000,
          });
        } else {
          setShowMembership(true);
        }
      } else {
        toast.error(error.response?.data?.detail || 'Failed to send request');
      }
    }
  };

  const handleApproveAttendance = async (userId) => {
    try {
      await axios.post(`${API}/events/${eventId}/approve-attendance/${userId}`);
      toast.success('Attendance approved!');
      setAttendanceRequests(prev => prev.filter(r => r.user_id !== userId));
      fetchEvent();
      fetchAttendees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRejectAttendance = async (userId) => {
    try {
      await axios.post(`${API}/events/${eventId}/reject-attendance/${userId}`);
      toast.success('Request declined');
      setAttendanceRequests(prev => prev.filter(r => r.user_id !== userId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline');
    }
  };

  // Turn an integer byte count into "1.2 MB" / "812 KB".
  const formatFileSize = (bytes) => {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleUploadAttachment = async (fileList, target /* 'new' | 'edit' */) => {
    if (!fileList || !fileList.length) return;
    const files = Array.from(fileList);
    setUploadingAttachment(true);
    try {
      for (const f of files) {
        const form = new FormData();
        form.append('file', f);
        const res = await axios.post(
          `${API}/events/${eventId}/comments/upload-attachment`,
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        const attachment = res.data;
        if (target === 'edit') {
          setEditAttachments((prev) => [...prev, attachment]);
        } else {
          setNewAttachments((prev) => [...prev, attachment]);
        }
      }
      toast.success(files.length === 1 ? 'File attached' : `${files.length} files attached`);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to upload file';
      toast.error(detail);
    } finally {
      setUploadingAttachment(false);
      // Reset inputs so the same file can be re-picked after removal.
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
      if (editAttachmentInputRef.current) editAttachmentInputRef.current.value = '';
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!user) {
      setShowAuth(true);
      return;
    }

    if (!newComment.trim() && newAttachments.length === 0) return;

    try {
      await axios.post(`${API}/events/${eventId}/comments`, {
        text: newComment,
        attachments: newAttachments.length ? newAttachments : undefined,
      });
      setNewComment('');
      setNewAttachments([]);
      fetchComments();
      toast.success('Comment added');
    } catch (error) {
      if (error.response?.status === 403) {
        if (isIOSApp) {
          toast.info('Membership required', {
            description: 'Visit expatssphere.com in your browser to upgrade and comment.',
            duration: 8000,
          });
        } else {
          setShowMembership(true);
        }
      } else {
        toast.error('Failed to add comment');
      }
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editCommentText.trim() && editAttachments.length === 0) return;
    try {
      await axios.put(`${API}/events/${eventId}/comments/${commentId}`, {
        text: editCommentText,
        // Always send the current list — including [] to clear all attachments.
        attachments: editAttachments,
      });
      setEditingCommentId(null);
      setEditCommentText('');
      setEditAttachments([]);
      fetchComments();
      toast.success('Comment updated');
    } catch (error) {
      const msg = error.response?.data?.detail || (error.response ? 'Failed to update comment' : 'Network error — please check your connection and try again');
      toast.error(msg);
    }
  };

  const handleTogglePinRecapComment = async (commentId) => {
    // If already pinned, unpin. Otherwise pin this one (and implicitly
    // replace any previously pinned comment on this event).
    const isCurrentlyPinned = event?.pinned_recap_comment_id === commentId;
    const payloadId = isCurrentlyPinned ? null : commentId;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/events/${eventId}/recap/pin-comment`,
        { comment_id: payloadId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isCurrentlyPinned ? 'Unpinned from recap' : 'Pinned as recap quote');
      // Optimistic patch + refresh so both the badge and the Wrapped card update.
      setEvent(prev => prev ? { ...prev, pinned_recap_comment_id: response.data.pinned_recap_comment_id, recap: response.data.recap } : prev);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pinned comment');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await axios.delete(`${API}/events/${eventId}/comments/${commentId}`);
      fetchComments();
      toast.success('Comment deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete comment');
    }
  };

  const handleSaveDescription = async () => {
    try {
      await axios.patch(`${API}/events/${eventId}/description`, { description: editDescriptionText });
      setEvent(prev => ({ ...prev, description: editDescriptionText }));
      setEditingDescription(false);
      toast.success('Description updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update description');
    }
  };

  const handleDelete = () => {
    // Open the rescue dialog so the host can either plain-cancel or
    // rebook to a new date in one tap.
    setRebookDate('');
    setRebookTime('');
    setRebookNote('');
    setShowDeleteDialog(true);
  };

  const handlePlainCancel = async () => {
    setCancelBusy(true);
    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success('Event cancelled. RSVPs notified.');
      setShowDeleteDialog(false);
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to cancel event');
    } finally {
      setCancelBusy(false);
    }
  };

  const handleRebook = async () => {
    if (!rebookDate || !rebookTime) {
      toast.error('Pick a new date and time first');
      return;
    }
    if (rebookDate === event?.event_date && rebookTime === event?.event_time) {
      toast.error('New date/time must differ from the current one');
      return;
    }
    setRebookBusy(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/rebook`, {
        event_date: rebookDate,
        event_time: rebookTime,
        message: rebookNote.trim() || undefined,
      });
      toast.success(res.data.message || 'Rebooked and invited.');
      setShowDeleteDialog(false);
      // Jump straight to the new event so the host can tweak anything else.
      const newId = res.data.new_event_id;
      navigate(newId ? `/events/${newId}` : '/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rebook event');
    } finally {
      setRebookBusy(false);
    }
  };

  const handleSendReminder = async () => {
    // Countdown signal: if the event is 7+ days away, show a soft confirm
    // so an over-eager creator doesn't fire the manual nudge weeks early
    // (and dilute the automatic 24h-before reminder).
    if (event?.event_date && !sendingReminder) {
      try {
        const ev = new Date(event.event_date + 'T00:00:00');
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const days = Math.round((ev - today) / (1000 * 60 * 60 * 24));
        if (days >= 7) {
          const ok = window.confirm(
            `Your event is ${days} days away.\n\n` +
            `Everyone attending automatically gets a reminder 24 hours before the event — sending one now might dilute that last-day nudge.\n\n` +
            `Tap OK to send now anyway, or Cancel to let the automatic 24h reminder do the work.`
          );
          if (!ok) return;
        }
      } catch { /* fall through to send if date parse fails */ }
    }

    setSendingReminder(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/events/${eventId}/send-reminder`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || 'Reminder sent!');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to send reminder';
      toast.error(detail);
    } finally {
      setSendingReminder(false);
    }
  };

  const handlePromoteEvent = async () => {
    setPromoting(true);
    try {
      const payload = {};
      if (promoteNote.trim()) payload.note = promoteNote.trim();
      if (promoteScope === 'group') {
        payload.scope = 'group';
        if (promoteGroupSlugs.length) payload.target_group_slugs = promoteGroupSlugs;
      }
      const response = await axios.post(`${API}/events/${eventId}/promote`, payload);
      toast.success(response.data.message || 'Event promoted!');
      setShowPromoteDialog(false);
      setPromoteNote('');
      setPromoteScope('city');
      setPromoteGroupSlugs([]);
      fetchEvent();
      fetchPromotionHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to promote event');
    } finally {
      setPromoting(false);
    }
  };

  const fetchPromotionHistory = async () => {
    if (!eventId) return;
    try {
      const res = await axios.get(`${API}/events/${eventId}/promotion-history`);
      setPromotionHistory(res.data?.promotions || []);
    } catch {
      setPromotionHistory([]);
    }
  };

  const handleNudgeAttendees = async () => {
    const trimmed = nudgeNote.trim();
    if (!trimmed) {
      toast.error('Add a short note so attendees know why you\'re nudging them.');
      return;
    }
    setNudging(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/nudge-attendees`, {
        note: trimmed,
        include_waitlist: nudgeIncludeWaitlist,
      });
      toast.success(res.data?.message || 'Attendees nudged');
      setShowNudgeDialog(false);
      setNudgeNote('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to nudge attendees');
    } finally {
      setNudging(false);
    }
  };

  // Handle edit image
  const handleEditImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image too large. Max size is 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewImageFile(reader.result);
        setNewImageUrl('');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveEventImage = async () => {
    if (!newImageFile && !newImageUrl) {
      toast.error('Please select an image or enter a URL');
      return;
    }

    setSavingImage(true);
    try {
      const imageToSave = newImageFile || newImageUrl;
      
      // Use the regular event update endpoint
      await axios.put(`${API}/events/${eventId}`, {
        ...event,
        image_url: imageToSave,
      });
      
      toast.success('Event image updated!');
      setShowEditImage(false);
      setNewImageFile(null);
      setNewImageUrl('');
      fetchEvent(); // Refresh event data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update image');
    } finally {
      setSavingImage(false);
    }
  };

  const handleDismiss = async () => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    
    setIsDismissing(true);
    try {
      await axios.post(`${API}/events/${eventId}/dismiss`);
      toast.success("Event dismissed. You won't see this event again.");
      navigate('/events');
    } catch (error) {
      toast.error('Failed to dismiss event');
    } finally {
      setIsDismissing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  if (!event) return null;

  const isCreator = user && (user.user_id === event.creator_id || user.user_id === event.organizer_id);
  const isAttending = user && event.attendees.includes(user.user_id);

  // Is this event already in the past (comparing viewer-local dates)?
  // Recap-photo flow: after an event has happened, any authenticated user
  // can share photos — RSVP records get lossy in the real world (people
  // showed up but forgot to hit RSVP, network hiccups, moderation, etc.)
  // and the social-pressure rationale of "RSVP first" doesn't apply once
  // the event is over. This is critical for community memory.
  const eventDateStr = (event.event_date || '').slice(0, 10);
  const todayStr = new Date().toISOString().split('T')[0];
  const isPastEvent = Boolean(eventDateStr) && eventDateStr < todayStr;

  // Creator + attendees can always upload; site admins get a fallback too so
  // moderators can seed a recap. For PAST events any authenticated user can
  // upload recap photos (the event has happened — anyone who was there
  // should be able to contribute to the memory).
  const canUploadPhotos =
    isCreator ||
    isAttending ||
    (user && user.is_admin) ||
    (user && isPastEvent);
  const totalGuests = event.guest_counts ? Object.values(event.guest_counts).reduce((a, b) => a + (parseInt(b) || 0), 0) : 0;
  const totalSeatsTaken = event.attendees.length + totalGuests;
  const isFull = event.max_attendees && totalSeatsTaken >= event.max_attendees;
  const myGuestsCount = (user && event.guest_counts) ? (event.guest_counts[user.user_id] || 0) : 0;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Back button — matches the pattern on profile / job / business / listing
              detail pages so users always have an in-app way to return. Uses
              location.state.label when set by the navigating page; otherwise
              defaults to "Events". Calls navigate(-1) so the list page's cache
              + scroll restoration fires; falls back to /events on direct link. */}
          <button
            type="button"
            onClick={() => {
              if (window.history.length > 1) navigate(-1);
              else navigate('/events');
            }}
            className="inline-flex items-center gap-1.5 mb-6 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            data-testid="event-detail-back-btn"
          >
            <ChevronLeft className="w-4 h-4" />
            {`Back to ${location.state?.label || 'Events'}`}
          </button>
          {/* Event Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-white rounded-3xl overflow-hidden border-2 border-border brutal-shadow mb-8">
              <div className="h-80 overflow-hidden bg-gray-100">
                <img
                  src={
                    event.image_url ||
                    'https://images.unsplash.com/photo-1719508784075-a49e1dcc0c0e?crop=entropy&cs=srgb&fm=jpg&q=85'
                  }
                  alt={event.title}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-8 md:p-12">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="px-4 py-2 bg-accent text-accent-foreground text-sm font-bold rounded-full">
                    {event.group_name || event.category}
                  </span>
                  {event.is_invite_only && (
                    <span className="px-4 py-2 bg-purple-100 text-purple-700 text-sm font-bold rounded-full">
                      Invite Only
                    </span>
                  )}
                  {isCreator && (
                    <span className="px-4 py-2 bg-primary text-primary-foreground text-sm font-bold rounded-full">
                      You're Hosting
                    </span>
                  )}
                </div>
                <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6">{event.title}</h1>
                
                {/* Description with rich text support */}
                <div className="text-lg text-muted-foreground mb-8">
                  {editingDescription ? (
                    <div>
                      <Textarea
                        value={editDescriptionText}
                        onChange={(e) => setEditDescriptionText(e.target.value)}
                        className="rounded-xl border-2 min-h-[150px] mb-3 text-base"
                        data-testid="edit-description-input"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleSaveDescription} data-testid="save-description">Save</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingDescription(false)} data-testid="cancel-description">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="relative group">
                      <div className="whitespace-pre-line">
                        {event.description?.split('\n').map((paragraph, index) => {
                          const trimmed = paragraph.trim();
                          if (trimmed.startsWith('•') || trimmed.startsWith('-') || trimmed.startsWith('*')) {
                            return (
                              <div key={index} className="flex items-start gap-2 ml-4 my-1">
                                <span className="text-primary mt-1">•</span>
                                <span>{trimmed.substring(1).trim()}</span>
                              </div>
                            );
                          }
                          return paragraph ? (
                            <p key={index} className="mb-3">{paragraph}</p>
                          ) : (
                            <br key={index} />
                          );
                        })}
                      </div>
                      {isCreator && (
                        <button
                          onClick={() => { setEditDescriptionText(event.description || ''); setEditingDescription(true); }}
                          className="mt-2 inline-flex items-center gap-1 text-sm text-primary hover:underline"
                          data-testid="edit-description-button"
                        >
                          <Edit className="w-3.5 h-3.5" />
                          Edit description
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mr-4">
                      <MapPin className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Location</div>
                      {event.location_hidden ? (
                        <>
                          <div className="font-semibold text-muted-foreground">
                            {event.city}, {event.country}
                          </div>
                          <div className="flex items-center gap-2 mt-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                            <EyeOff className="w-4 h-4 text-purple-600" />
                            <span className="text-sm text-purple-700">
                              Exact location revealed after your request is approved
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="font-semibold">
                            {event.city}, {event.country}
                          </div>
                          <div className="text-sm text-muted-foreground">{event.location_details}</div>
                          {event.map_link && (
                            <a
                              href={event.map_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-1"
                              data-testid="map-link"
                            >
                              <MapPin className="w-3 h-3" />
                              View on Map
                            </a>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mr-4">
                      <Calendar className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Date & Time</div>
                      <div className="font-semibold">{event.event_date}</div>
                      <div className="text-sm text-muted-foreground">{event.event_time}</div>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mr-4">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      {event.is_shared_listing ? (
                        <>
                          <div className="text-sm text-muted-foreground">Hosted by:</div>
                          <div className="font-bold" data-testid="event-external-host-name">
                            {event.external_host_name || 'External organizer'}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Shared by <span className="font-medium">{event.creator_name}</span> · community pick
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-sm text-muted-foreground">Hosted by:</div>
                          <div className="font-bold">{event.creator_name}</div>
                        </>
                      )}
                    </div>
                  </div>
                  {event.is_shared_listing && event.external_source_url && (
                    <a
                      href={event.external_source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full h-12 rounded-xl bg-amber-500 text-white font-bold hover:bg-amber-600 transition-colors"
                      data-testid="event-external-source-cta"
                    >
                      <Ticket className="w-4 h-4" />
                      Book / more info
                    </a>
                  )}
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mr-4">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-muted-foreground">
                        {showAttendees ? 'Attendees' : 'Attendees'}
                      </div>
                      <div className="font-semibold" data-testid="attendees-count-line">
                        {totalSeatsTaken}
                        {event.max_attendees && ` / ${event.max_attendees}`} attending
                        {event.allow_guests && totalGuests > 0 && (
                          <span className="text-xs text-muted-foreground ml-1" data-testid="attendees-breakdown">
                            ({event.attendees.length} member{event.attendees.length !== 1 ? 's' : ''} + {totalGuests} guest{totalGuests !== 1 ? 's' : ''})
                          </span>
                        )}
                      </div>
                      {user && event.attendees && event.attendees.length > 0 && (
                        <button
                          onClick={handleShowAttendees}
                          className="text-sm text-primary hover:underline mt-1 flex items-center gap-1"
                          data-testid="view-attendees-button"
                        >
                          {showAttendees ? 'Hide attendee list' : 'View attendee list'}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Attendees List - Visible to all logged-in members */}
                  {user && showAttendees && (
                    <div className="bg-primary/5 rounded-xl p-4 mt-2">
                      <h4 className="font-bold mb-3 text-sm">Attendees ({event.attendees.length})</h4>
                      {attendees.length > 0 ? (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {attendees.map((attendee, index) => (
                            <div 
                              key={attendee.user_id || index} 
                              className="flex items-center gap-3 p-2 bg-white rounded-lg hover:bg-gray-50"
                            >
                              <div
                                className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                                onClick={() => navigate(`/profile/${attendee.user_id}`, { state: { from: 'event', label: 'Event' } })}
                              >
                                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary shrink-0">
                                  {attendee.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">
                                    {attendee.name}
                                    {event.guest_counts && event.guest_counts[attendee.user_id] > 0 && (
                                      <span
                                        className="ml-2 inline-flex items-center rounded-full bg-sky-100 text-sky-700 text-[10px] font-bold px-2 py-0.5"
                                        data-testid={`attendee-guests-badge-${attendee.user_id}`}
                                      >
                                        +{event.guest_counts[attendee.user_id]}
                                      </span>
                                    )}
                                  </p>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {attendee.nationality && `${attendee.nationality}`}
                                    {attendee.nationality && attendee.residence_city && ' • '}
                                    {attendee.residence_city && attendee.residence_city}
                                  </p>
                                  {isCreator && event.guest_notes && event.guest_notes[attendee.user_id] && (
                                    <p className="text-xs italic text-sky-700 truncate mt-0.5" title={event.guest_notes[attendee.user_id]} data-testid={`attendee-notes-${attendee.user_id}`}>
                                      📝 {event.guest_notes[attendee.user_id]}
                                    </p>
                                  )}
                                </div>
                              </div>
                              {isCreator && event.allow_guests && attendee.user_id !== user.user_id && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setHostGuestEditor({
                                      user_id: attendee.user_id,
                                      name: attendee.name || 'this attendee',
                                      current: (event.guest_counts || {})[attendee.user_id] || 0,
                                    });
                                  }}
                                  className="text-xs font-semibold text-sky-700 hover:text-sky-900 rounded-full border border-sky-300 hover:bg-sky-50 px-2 py-1 shrink-0"
                                  data-testid={`host-edit-guests-${attendee.user_id}`}
                                >
                                  Edit +N
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-muted-foreground">
                          <p>Loading attendees...</p>
                        </div>
                      )}
                      {isCreator && (event.waitlist || []).length > 0 && (
                        <div className="mt-4 pt-3 border-t border-amber-200">
                          <h4 className="font-bold mb-2 text-sm text-amber-800" data-testid="host-waitlist-heading">
                            Waitlist ({(event.waitlist || []).length})
                          </h4>
                          <p className="text-xs text-amber-700 mb-2">
                            Auto-promoted in order when seats open up.
                          </p>
                          <ol className="text-xs list-decimal pl-5 space-y-1 text-amber-900" data-testid="host-waitlist-list">
                            {(event.waitlist || []).map((uid, i) => {
                              const info = waitlistUsers.find((a) => a.user_id === uid) || attendees.find((a) => a.user_id === uid);
                              return (
                                <li key={uid}>
                                  {info?.name || uid.slice(0, 8) + '…'}
                                  {info?.residence_city && (
                                    <span className="text-amber-700 ml-1">• {info.residence_city}</span>
                                  )}
                                </li>
                              );
                            })}
                          </ol>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Attendance Requests - Only visible to event creator for invite-only events */}
                  {isCreator && event.is_invite_only && attendanceRequests.length > 0 && (
                    <div className="bg-purple-50 rounded-xl p-4 mt-4 border-2 border-purple-200">
                      <h4 className="font-bold mb-3 text-sm text-purple-800">
                        Pending Requests ({attendanceRequests.length})
                      </h4>
                      <div className="space-y-3">
                        {attendanceRequests.map((requester) => (
                          <div 
                            key={requester.user_id} 
                            className="flex items-center gap-3 p-3 bg-white rounded-lg"
                          >
                            <div 
                              className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-sm font-bold text-purple-700 cursor-pointer"
                              onClick={() => navigate(`/profile/${requester.user_id}`)}
                            >
                              {requester.profile_picture ? (
                                <img src={requester.profile_picture} alt={requester.name} className="w-10 h-10 rounded-full object-cover" />
                              ) : (
                                requester.name?.charAt(0)?.toUpperCase() || '?'
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p 
                                className="font-medium text-sm truncate cursor-pointer hover:text-primary"
                                onClick={() => navigate(`/profile/${requester.user_id}`)}
                              >
                                {requester.name}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {requester.residence_city}, {requester.residence_country}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleApproveAttendance(requester.user_id)}
                                className="rounded-full bg-green-600 hover:bg-green-700"
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRejectAttendance(requester.user_id)}
                                className="rounded-full"
                              >
                                Decline
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Buy Tickets Button */}
                {event.ticket_url && (
                  <div className="mt-6 mb-2 p-4 bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl border-2 border-primary/20">
                    <a
                      href={event.ticket_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-full font-bold transition-colors"
                      data-testid="buy-tickets-button"
                    >
                      <Ticket className="w-5 h-5" />
                      Buy Tickets
                    </a>
                    <p className="text-xs text-center text-muted-foreground mt-2">
                      You'll be redirected to an external ticket provider
                    </p>
                  </div>
                )}

                <div className="flex flex-wrap gap-4">
                  {isCreator ? (
                    <>
                      {!event.allow_guests && !isPastEvent && (
                        <Button
                          onClick={handleEnableGuestsForEvent}
                          variant="outline"
                          className="h-12 px-6 rounded-full font-bold border-2 border-sky-400 text-sky-700 hover:bg-sky-50"
                          data-testid="enable-guests-inline-btn"
                          title="Let attendees bring a +1"
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Enable +1 Guests
                        </Button>
                      )}
                      <Button
                        onClick={() => navigate(`/edit-event/${event.event_id}`)}
                        className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-full font-bold"
                        data-testid="edit-event-button"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Event
                      </Button>
                      <Button
                        onClick={handleSendReminder}
                        disabled={sendingReminder}
                        variant="outline"
                        className="h-12 px-8 rounded-full font-bold border-2 border-primary text-primary hover:bg-primary/10"
                        data-testid="send-reminder-button"
                      >
                        <Bell className="w-4 h-4 mr-2" />
                        {sendingReminder ? 'Sending...' : 'Send Reminder'}
                      </Button>
                      <Button
                        onClick={handleSaveAsTemplate}
                        variant="outline"
                        className="h-12 px-6 rounded-full font-bold border-2 border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                        data-testid="save-as-template-btn"
                        title="Save this event's setup as a reusable template"
                      >
                        <BookmarkPlus className="w-4 h-4 mr-2" />
                        Save as Template
                      </Button>
                      <Button
                        onClick={handleDelete}
                        variant="destructive"
                        className="h-12 px-8 rounded-full font-bold"
                        data-testid="delete-event-button"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Event
                      </Button>
                    </>
                  ) : isAttending ? (
                    <div className="flex flex-col gap-2" data-testid="attending-controls">
                      {event.allow_guests && (
                        <div className="flex items-center gap-2 bg-sky-50 border-2 border-sky-200 rounded-full px-3 py-1.5" data-testid="my-guests-stepper">
                          <span className="text-sm text-sky-800 font-semibold">Bringing:</span>
                          <button
                            type="button"
                            onClick={() => handleUpdateGuestCount(myGuestsCount - 1)}
                            disabled={updatingGuests || myGuestsCount <= 0}
                            className="w-7 h-7 rounded-full bg-white border border-sky-300 text-sky-700 font-bold disabled:opacity-40"
                            data-testid="my-guests-decrement"
                            aria-label="Decrease guest count"
                          >
                            −
                          </button>
                          <span className="text-sm font-bold text-sky-900 min-w-[1.5rem] text-center" data-testid="my-guests-count">
                            {myGuestsCount}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleUpdateGuestCount(myGuestsCount + 1)}
                            disabled={updatingGuests || (event.max_guests_per_rsvp != null && myGuestsCount >= event.max_guests_per_rsvp) || isFull}
                            className="w-7 h-7 rounded-full bg-sky-600 text-white font-bold disabled:opacity-40"
                            data-testid="my-guests-increment"
                            aria-label="Increase guest count"
                          >
                            +
                          </button>
                          <span className="text-xs text-sky-700">
                            {event.max_guests_per_rsvp != null ? `guest${event.max_guests_per_rsvp !== 1 ? 's' : ''} (max ${event.max_guests_per_rsvp})` : 'guests'}
                          </span>
                        </div>
                      )}
                      {event.allow_guests && myGuestsCount > 0 && (
                        <div className="flex flex-col gap-1 w-full" data-testid="my-guest-notes-wrap">
                          <textarea
                            defaultValue={(event.guest_notes || {})[user.user_id] || ''}
                            onBlur={(e) => {
                              const next = e.target.value.trim();
                              const current = ((event.guest_notes || {})[user.user_id] || '').trim();
                              if (next !== current) handleSaveMyNotes(next);
                            }}
                            placeholder="Guest names, dietary needs, kids' ages — visible only to the host (auto-saves on blur)"
                            className="w-full rounded-xl border-2 border-sky-200 px-3 py-2 text-sm bg-white focus:border-sky-400 focus:outline-none resize-none"
                            rows={2}
                            maxLength={500}
                            data-testid="my-guest-notes"
                          />
                          {savingNotes && <span className="text-xs text-sky-600">Saving…</span>}
                        </div>
                      )}
                      <Button
                        onClick={handleCancelRSVP}
                        variant="outline"
                        className="h-12 px-8 rounded-full font-bold border-2"
                        data-testid="cancel-rsvp-button"
                      >
                        Cancel RSVP
                      </Button>
                    </div>
                  ) : event.is_invite_only ? (
                    hasRequestedAttendance ? (
                      <Button
                        disabled
                        variant="outline"
                        className="h-12 px-8 rounded-full font-bold border-2"
                        data-testid="pending-request-button"
                      >
                        Request Pending...
                      </Button>
                    ) : (
                      <Button
                        onClick={handleRequestAttendance}
                        disabled={isFull}
                        className="bg-accent text-accent-foreground hover:bg-accent/90 h-12 px-8 rounded-full font-bold brutal-shadow"
                        data-testid="request-attendance-button"
                      >
                        {isFull ? 'Event Full' : 'Request to Join'}
                      </Button>
                    )
                  ) : (event.waitlist || []).includes(user?.user_id) ? (
                    <div className="flex flex-col gap-2" data-testid="on-waitlist-controls">
                      <div className="px-4 py-2 rounded-full bg-amber-100 border-2 border-amber-300 text-amber-800 text-sm font-semibold" data-testid="waitlist-position-badge">
                        On the waitlist — position #{(event.waitlist || []).indexOf(user.user_id) + 1}
                      </div>
                      <Button
                        onClick={handleLeaveWaitlist}
                        variant="outline"
                        className="h-12 px-8 rounded-full font-bold border-2"
                        data-testid="leave-waitlist-button"
                      >
                        Leave Waitlist
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2" data-testid="rsvp-controls">
                      {event.allow_guests && !isFull && (
                        <div className="flex items-center gap-2 bg-sky-50 border-2 border-sky-200 rounded-full px-3 py-1.5" data-testid="pre-rsvp-guests-stepper">
                          <span className="text-sm text-sky-800 font-semibold">Bringing anyone?</span>
                          <button
                            type="button"
                            onClick={() => setPendingGuestsCount((c) => Math.max(0, c - 1))}
                            disabled={pendingGuestsCount <= 0}
                            className="w-7 h-7 rounded-full bg-white border border-sky-300 text-sky-700 font-bold disabled:opacity-40"
                            data-testid="pre-rsvp-guests-decrement"
                            aria-label="Decrease guest count"
                          >
                            −
                          </button>
                          <span className="text-sm font-bold text-sky-900 min-w-[1.5rem] text-center" data-testid="pre-rsvp-guests-count">
                            {pendingGuestsCount}
                          </span>
                          <button
                            type="button"
                            onClick={() => setPendingGuestsCount((c) => {
                              const max = event.max_guests_per_rsvp ?? 99;
                              return Math.min(max, c + 1);
                            })}
                            disabled={event.max_guests_per_rsvp != null && pendingGuestsCount >= event.max_guests_per_rsvp}
                            className="w-7 h-7 rounded-full bg-sky-600 text-white font-bold disabled:opacity-40"
                            data-testid="pre-rsvp-guests-increment"
                            aria-label="Increase guest count"
                          >
                            +
                          </button>
                          <span className="text-xs text-sky-700">
                            {event.max_guests_per_rsvp != null ? `guest${event.max_guests_per_rsvp !== 1 ? 's' : ''} (max ${event.max_guests_per_rsvp})` : 'guests (any number)'}
                          </span>
                        </div>
                      )}
                      {event.allow_guests && !isFull && pendingGuestsCount > 0 && (
                        <textarea
                          value={pendingGuestNotes}
                          onChange={(e) => setPendingGuestNotes(e.target.value.slice(0, 500))}
                          placeholder="Optional: guest names, dietary needs, kids' ages — visible only to the host"
                          className="w-full rounded-xl border-2 border-sky-200 px-3 py-2 text-sm bg-white focus:border-sky-400 focus:outline-none resize-none"
                          rows={2}
                          maxLength={500}
                          data-testid="pre-rsvp-guest-notes"
                        />
                      )}
                      <Button
                        onClick={isFull ? handleJoinWaitlist : () => handleRSVP()}
                        className={isFull
                          ? "bg-amber-500 text-white hover:bg-amber-600 h-12 px-8 rounded-full font-bold brutal-shadow"
                          : "bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-full font-bold brutal-shadow"}
                        data-testid={isFull ? "join-waitlist-button" : "rsvp-button"}
                      >
                        {isFull
                          ? 'Join Waitlist'
                          : (pendingGuestsCount > 0 ? `RSVP + ${pendingGuestsCount} guest${pendingGuestsCount !== 1 ? 's' : ''}` : 'RSVP Now')}
                      </Button>
                    </div>
                  )}
                  
                  {/* Save Button */}
                  <SaveButton 
                    itemType="event" 
                    itemId={event.event_id}
                    className="h-12 px-6 rounded-full"
                  />
                  
                  {/* Share Button — uses the backend OG-preview URL so social
                      platforms (WhatsApp / Messenger / Telegram / Twitter / etc.)
                      render rich preview cards with the event title, date,
                      image, and location. Humans tapping the link are
                      auto-redirected to the React route via meta-refresh. */}
                  <Button
                    onClick={() => {
                      const shareUrl = `${window.location.origin}/api/share/event/${event.event_id}`;
                      const shareText = `Check out this event: ${event.title}`;

                      if (navigator.share) {
                        // Use native share on mobile
                        navigator.share({
                          title: event.title,
                          text: shareText,
                          url: shareUrl,
                        }).catch(() => {
                          // Fallback to clipboard
                          navigator.clipboard.writeText(shareUrl);
                          toast.success('Link copied to clipboard!');
                        });
                      } else {
                        // Copy to clipboard on desktop
                        navigator.clipboard.writeText(shareUrl);
                        toast.success('Link copied to clipboard!');
                      }
                    }}
                    variant="outline"
                    className="h-12 px-6 rounded-full border-2"
                    data-testid="share-event-button"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>

                  {/* Quick-share deep links: WhatsApp + Telegram.
                      Pre-fills the user's message composer so a one-tap share
                      lands in an existing chat without leaving their messenger. */}
                  {(() => {
                    const shareUrl = `${window.location.origin}/api/share/event/${event.event_id}`;
                    const text = `Check out this event: ${event.title} — ${shareUrl}`;
                    const waHref = `https://wa.me/?text=${encodeURIComponent(text)}`;
                    const tgHref = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent('Check out this event: ' + event.title)}`;
                    return (
                      <>
                        <a
                          href={waHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center h-12 px-5 rounded-full border-2 border-green-500 text-green-600 font-semibold hover:bg-green-50 transition-colors"
                          aria-label="Share on WhatsApp"
                          data-testid="share-whatsapp-btn"
                        >
                          <MessageCircle className="w-4 h-4 mr-2" />
                          WhatsApp
                        </a>
                        <a
                          href={tgHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center h-12 px-5 rounded-full border-2 border-sky-500 text-sky-600 font-semibold hover:bg-sky-50 transition-colors"
                          aria-label="Share on Telegram"
                          data-testid="share-telegram-btn"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Telegram
                        </a>
                      </>
                    );
                  })()}
                  
                  {/* Promote Button - Only for event creator */}
                  {isCreator && (
                    <>
                    <Button
                      onClick={() => setShowPromoteDialog(true)}
                      disabled={promoting}
                      variant="outline"
                      className="h-12 px-6 rounded-full border-2 border-primary text-primary hover:bg-primary/10"
                      data-testid="promote-event-button"
                    >
                      <Megaphone className="w-4 h-4 mr-2" />
                      {promoting ? 'Promoting...' : event.last_promoted_at ? 'Promote Again' : 'Promote'}
                    </Button>
                    {((event.attendees?.length || 0) > 0 || (event.waitlist?.length || 0) > 0) && (
                      <Button
                        onClick={() => setShowNudgeDialog(true)}
                        variant="outline"
                        className="h-12 px-6 rounded-full border-2 border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                        data-testid="nudge-attendees-button"
                        title="Send a private message to attendees — no public cooldown"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Nudge Attendees
                      </Button>
                    )}
                    {promotionHistory.length > 0 && (
                      <div
                        className="w-full mt-2 rounded-2xl border-2 border-border bg-gradient-to-r from-primary/5 to-accent/5 p-4"
                        data-testid="promotion-analytics-card"
                      >
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <div className="text-sm font-semibold">
                            Last promotion&nbsp;·&nbsp;
                            <span className="text-muted-foreground font-normal">
                              {(() => {
                                const d = new Date(promotionHistory[0].created_at);
                                const diffDays = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
                                return diffDays === 0 ? 'today' : `${diffDays}d ago`;
                              })()}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowHistoryDialog(true)}
                            className="text-xs font-semibold text-primary hover:underline"
                            data-testid="promotion-history-link"
                          >
                            View history ({promotionHistory.length}) →
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="rounded-lg bg-white border border-border py-2">
                            <div className="text-lg font-bold">{promotionHistory[0].recipient_count}</div>
                            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Reached</div>
                          </div>
                          <div className="rounded-lg bg-sky-50 border border-sky-100 py-2">
                            <div className="text-lg font-bold text-sky-700">{promotionHistory[0].views_count}</div>
                            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Opened</div>
                          </div>
                          <div className="rounded-lg bg-emerald-50 border border-emerald-100 py-2">
                            <div className="text-lg font-bold text-emerald-700">{promotionHistory[0].rsvps_count}</div>
                            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">RSVP'd</div>
                          </div>
                        </div>
                      </div>
                    )}
                    </>
                  )}
                  
                  {/* Report Button - Only show to logged in users who aren't the creator */}
                  {user && !isCreator && (
                    <Button
                      onClick={() => setShowReport(true)}
                      variant="ghost"
                      className="h-12 px-6 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      data-testid="report-event-button"
                    >
                      <Flag className="w-4 h-4 mr-2" />
                      Report
                    </Button>
                  )}
                  
                  {/* Dismiss Button - Only show to logged in users who aren't the creator */}
                  {user && !isCreator && (
                    <Button
                      onClick={handleDismiss}
                      disabled={isDismissing}
                      variant="ghost"
                      className="h-12 px-6 rounded-full text-muted-foreground hover:text-orange-600 hover:bg-orange-50"
                      data-testid="dismiss-event-button"
                    >
                      <EyeOff className="w-4 h-4 mr-2" />
                      {isDismissing ? 'Dismissing...' : 'Not Interested'}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Recap Wrapped card removed per host feedback (Oct 2026):
                auto-curated quotes could pick an awkward "I'll be there"
                reply, and photos are already surfaced in the Photo Gallery
                below. Backend recap endpoint kept dormant for future revival. */}

            {/* Photo Gallery Section */}
            <div id="photos" className="bg-white rounded-3xl p-8 md:p-12 border-2 border-border scroll-mt-24">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-heading font-bold flex items-center">
                  <Image className="w-6 h-6 mr-3" />
                  Event Photos ({photos.length})
                </h2>
                {canUploadPhotos && (
                  <Button
                    onClick={() => setShowPhotoUpload(!showPhotoUpload)}
                    variant="outline"
                    className="rounded-full"
                    data-testid="event-add-photo-btn"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Add Photo
                  </Button>
                )}
              </div>

              {/* Host-only moderation hint — only visible when there are photos
                  to moderate. Keeps the grid clean for members but makes the
                  tap-to-delete flow discoverable for hosts/admins. */}
              {photos.length > 0 && (isCreator || user?.is_admin) && (
                <div
                  className="mb-6 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900"
                  data-testid="host-photo-moderation-hint"
                >
                  <span className="text-base leading-none mt-0.5">💡</span>
                  <span>
                    <strong>Host tip:</strong> tap any photo to open it full-size — you'll see a <em>Delete Photo</em> button in the lightbox to remove inappropriate uploads.
                  </span>
                </div>
              )}

              {/* Share row for the photo gallery itself — mirrors the recap
                  card's share options so members can push the album to
                  Instagram / WhatsApp / Telegram without leaving the page.
                  Only shown once the gallery has at least one photo. */}
              {photos.length > 0 && (
                <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="event-photos-share-row">
                  <span className="text-xs uppercase font-semibold tracking-wide text-muted-foreground mr-1">
                    Share album:
                  </span>
                  <Button
                    size="sm"
                    onClick={async () => {
                      const url = `${window.location.origin}/events/${event.event_id}#photos`;
                      const text = `${photos.length} photo${photos.length === 1 ? '' : 's'} from ${event.title}`;
                      if (navigator.share) {
                        try {
                          await navigator.share({ title: event.title, text, url });
                          return;
                        } catch (_) { /* user cancelled */ }
                      }
                      try {
                        await navigator.clipboard.writeText(url);
                        toast.success('Link copied — paste it anywhere');
                      } catch (_) {
                        toast.error('Copy failed');
                      }
                    }}
                    className="rounded-full bg-primary hover:bg-primary/90 gap-2"
                    data-testid="event-photos-share-native"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const url = `${window.location.origin}/events/${event.event_id}#photos`;
                      const text = `${photos.length} photo${photos.length === 1 ? '' : 's'} from ${event.title}`;
                      window.open(`https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`, '_blank');
                    }}
                    className="rounded-full gap-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                    data-testid="event-photos-share-whatsapp"
                  >
                    WhatsApp
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const url = `${window.location.origin}/events/${event.event_id}#photos`;
                      const text = `${photos.length} photo${photos.length === 1 ? '' : 's'} from ${event.title}`;
                      window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
                    }}
                    className="rounded-full gap-2 border-sky-500 text-sky-700 hover:bg-sky-50"
                    data-testid="event-photos-share-telegram"
                  >
                    Telegram
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      // Open the picker dialog so the user chooses which
                      // photos go into the shareable collage, rather than
                      // auto-including every photo in the album.
                      if (!photos || photos.length === 0) {
                        toast.error('No photos to share yet');
                        return;
                      }
                      setSelectedDownloadIds([]);
                      setDownloadCaption('');
                      setCaptionStyle('quote');
                      setIncludeHeader(true);
                      setPreviewUrl('');
                      setShowDownloadDialog(true);
                    }}
                    className="rounded-full gap-2 border-fuchsia-500 text-fuchsia-700 hover:bg-fuchsia-50"
                    data-testid="event-photos-download-to-share"
                  >
                    <Download className="w-4 h-4" />
                    Download to Share
                  </Button>
                </div>
              )}

              {/* Photo Upload Form */}
              {showPhotoUpload && canUploadPhotos && (
                <div className="mb-6 p-4 bg-muted/50 rounded-xl">
                  <input
                    type="file"
                    accept={isIOSApp ? ".jpg,.jpeg,.png,.gif,.webp,.heic" : "image/*"}
                    multiple
                    onChange={handlePhotoUpload}
                    ref={photoInputRef}
                    className="hidden"
                  />
                  <Input
                    value={photoCaption}
                    onChange={(e) => setPhotoCaption(e.target.value)}
                    placeholder="Add a caption (applies to all photos below)"
                    className="mb-1"
                    data-testid="photo-caption-input"
                  />
                  <p className="text-xs text-muted-foreground mb-3 px-1">
                    Caption is applied to every photo you pick next.
                  </p>
                  <Button
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="w-full rounded-full"
                  >
                    {uploadingPhoto ? 'Uploading...' : 'Choose Photos'}
                  </Button>
                </div>
              )}

              {/* Photos Grid */}
              {photos.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {photos.map((photo) => (
                    <div
                      key={photo.photo_id}
                      onClick={() => setSelectedPhoto(photo)}
                      className="relative aspect-square rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                    >
                      <img
                        src={photo.image_data || photo.image_url}
                        alt={photo.caption || 'Event photo'}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Camera className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No photos yet</p>
                  {canUploadPhotos ? (
                    <p className="text-sm">{isPastEvent ? 'Share a memory from this event!' : 'Be the first to share a photo!'}</p>
                  ) : user ? (
                    <div className="mt-3 flex flex-col items-center gap-2" data-testid="event-photos-rsvp-nudge">
                      <p className="text-sm">RSVP to share photos with the group</p>
                      {!isAttending && !isCreator && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={() => {
                            const el =
                              document.getElementById('rsvp-section') ||
                              document.querySelector('[data-testid="rsvp-button"]') ||
                              document.querySelector('[data-testid="rsvp-now-btn"]');
                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }}
                          data-testid="event-photos-rsvp-cta"
                        >
                          RSVP Now
                        </Button>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm">Sign in and RSVP to share photos.</p>
                  )}
                </div>
              )}

              {/* Photo Lightbox with prev/next navigation */}
              {selectedPhoto && (
                <div
                  className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
                  onClick={() => setSelectedPhoto(null)}
                  onTouchStart={(e) => { e.currentTarget._touchX = e.touches[0].clientX; }}
                  onTouchEnd={(e) => {
                    const dx = e.changedTouches[0].clientX - (e.currentTarget._touchX || 0);
                    if (Math.abs(dx) > 60) {
                      const idx = photos.findIndex(p => p.photo_id === selectedPhoto.photo_id);
                      if (dx < 0 && idx < photos.length - 1) setSelectedPhoto(photos[idx + 1]);
                      if (dx > 0 && idx > 0) setSelectedPhoto(photos[idx - 1]);
                    }
                  }}
                  onKeyDown={(e) => {
                    const idx = photos.findIndex(p => p.photo_id === selectedPhoto.photo_id);
                    if (e.key === 'ArrowRight' && idx < photos.length - 1) setSelectedPhoto(photos[idx + 1]);
                    if (e.key === 'ArrowLeft' && idx > 0) setSelectedPhoto(photos[idx - 1]);
                    if (e.key === 'Escape') setSelectedPhoto(null);
                  }}
                  tabIndex={0}
                  ref={(el) => el?.focus()}
                >
                  <div className="relative w-full max-w-4xl mx-4" onClick={(e) => e.stopPropagation()}>
                    {/* Close button */}
                    <Button
                      onClick={() => setSelectedPhoto(null)}
                      variant="ghost"
                      className="absolute top-2 right-2 z-10 text-white hover:bg-white/20 rounded-full"
                      data-testid="photo-lightbox-close"
                    >
                      <X className="w-6 h-6" />
                    </Button>

                    {/* Previous arrow */}
                    {photos.findIndex(p => p.photo_id === selectedPhoto.photo_id) > 0 && (
                      <button
                        onClick={() => {
                          const idx = photos.findIndex(p => p.photo_id === selectedPhoto.photo_id);
                          setSelectedPhoto(photos[idx - 1]);
                        }}
                        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-r-xl transition-colors"
                        data-testid="photo-lightbox-prev"
                      >
                        <ChevronLeft className="w-8 h-8" />
                      </button>
                    )}

                    {/* Next arrow */}
                    {photos.findIndex(p => p.photo_id === selectedPhoto.photo_id) < photos.length - 1 && (
                      <button
                        onClick={() => {
                          const idx = photos.findIndex(p => p.photo_id === selectedPhoto.photo_id);
                          setSelectedPhoto(photos[idx + 1]);
                        }}
                        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-l-xl transition-colors"
                        data-testid="photo-lightbox-next"
                      >
                        <ChevronRight className="w-8 h-8" />
                      </button>
                    )}

                    {/* Photo */}
                    {/* Host-side review banner — only visible when the
                        current photo has pending reports. */}
                    {isCreator && currentPhotoReports.length > 0 && (
                      <div
                        className="mb-3 p-3 rounded-lg bg-yellow-100 border border-yellow-300 text-yellow-900 text-sm"
                        data-testid="photo-report-banner"
                      >
                        <div className="font-semibold flex items-center gap-1 mb-1">
                          <Flag className="w-4 h-4" />
                          Reported by {currentPhotoReports.length} attendee{currentPhotoReports.length === 1 ? '' : 's'}
                        </div>
                        <div className="text-xs">
                          Reason{currentPhotoReports.length === 1 ? '' : 's'}: {' '}
                          {[...new Set(currentPhotoReports.map((r) => r.reason || 'other'))].join(', ').replace(/_/g, ' ')}
                        </div>
                        {currentPhotoReports[0]?.note && (
                          <div className="text-xs italic mt-1">“{currentPhotoReports[0].note}”</div>
                        )}
                      </div>
                    )}

                    <img
                      src={selectedPhoto.image_data || selectedPhoto.image_url}
                      alt={selectedPhoto.caption || 'Event photo'}
                      className="w-full max-h-[80vh] object-contain rounded-lg"
                    />

                    {/* Caption + uploader + counter */}
                    <div className="text-center mt-3">
                      {selectedPhoto.caption && (
                        <p className="text-white mb-1">{selectedPhoto.caption}</p>
                      )}
                      <p className="text-white/60 text-sm">
                        By {selectedPhoto.uploader_name} — {photos.findIndex(p => p.photo_id === selectedPhoto.photo_id) + 1} of {photos.length}
                      </p>
                      <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
                        {(selectedPhoto.uploader_id === user?.user_id || isCreator || user?.is_admin) && (
                          <Button
                            onClick={() => handleDeletePhoto(selectedPhoto)}
                            variant="destructive"
                            size="sm"
                            data-testid="delete-photo-btn"
                          >
                            Delete Photo
                          </Button>
                        )}
                        {user
                          && selectedPhoto.uploader_id !== user?.user_id
                          && !isCreator
                          && !user?.is_admin && (
                          <Button
                            onClick={() => openReportDialog(selectedPhoto)}
                            variant="outline"
                            size="sm"
                            className="bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white"
                            data-testid="report-photo-btn"
                          >
                            <Flag className="w-4 h-4 mr-1" />
                            Report
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Comments Section */}
            <div id="comments-section" className="bg-white rounded-3xl p-8 md:p-12 border-2 border-border scroll-mt-24">
              <h2 className="text-2xl font-heading font-bold mb-6 flex items-center">
                <MessageCircle className="w-6 h-6 mr-3" />
                Discussion ({comments.length})
              </h2>

              {user && (
                <form id="add-comment" onSubmit={handleAddComment} className="mb-8 scroll-mt-24">
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="rounded-xl border-2 min-h-[100px] mb-3"
                    data-testid="comment-input"
                  />
                  {/* Attachment chips — preview before posting */}
                  {newAttachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3" data-testid="new-comment-attachments">
                      {newAttachments.map((att, idx) => (
                        <div key={idx} className="inline-flex items-center gap-2 px-3 py-1.5 bg-muted rounded-full text-sm border border-border">
                          <FileText className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                          <span className="max-w-[180px] truncate">{att.filename}</span>
                          <span className="text-xs text-muted-foreground">{formatFileSize(att.size_bytes)}</span>
                          <button
                            type="button"
                            onClick={() => setNewAttachments((prev) => prev.filter((_, i) => i !== idx))}
                            className="text-muted-foreground hover:text-destructive"
                            aria-label={`Remove ${att.filename}`}
                            data-testid={`remove-new-attachment-${idx}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,image/*"
                    multiple
                    onChange={(e) => handleUploadAttachment(e.target.files, 'new')}
                    data-testid="attach-file-input"
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      type="submit"
                      className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-full font-bold"
                      data-testid="comment-submit"
                      disabled={uploadingAttachment}
                    >
                      Post Comment
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => attachmentInputRef.current?.click()}
                      className="h-12 px-4 rounded-full flex items-center gap-2"
                      disabled={uploadingAttachment}
                      data-testid="attach-file-btn"
                      title="Attach a PDF, doc, or image (max 10MB)"
                    >
                      <Paperclip className="w-4 h-4" />
                      {uploadingAttachment ? 'Uploading…' : 'Attach file'}
                    </Button>
                    <span className="text-xs text-muted-foreground">PDF · DOC · XLS · PPT · TXT · images · up to 10MB</span>
                  </div>
                </form>
              )}

              <div className="space-y-6">
                {comments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No comments yet. Be the first to comment!
                  </div>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.comment_id} id={`comment-${comment.comment_id}`} className="border-b border-border pb-6 last:border-0 transition-all duration-500 rounded-lg scroll-mt-24" data-testid="comment-item">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center font-bold text-accent-foreground flex-shrink-0">
                          {comment.user_name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 min-w-0 flex-wrap">
                              <span className="font-semibold">{comment.user_name}</span>
                              <span className="text-sm text-muted-foreground">
                                {comment.created_at && format(new Date(comment.created_at), 'MMM d, yyyy')}
                              </span>
                              {comment.edited && <span className="text-xs text-muted-foreground italic">(edited)</span>}
                              {event?.pinned_recap_comment_id === comment.comment_id && (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-full bg-amber-100 text-amber-800 border border-amber-200"
                                  data-testid={`pinned-recap-badge-${comment.comment_id}`}
                                  title="Featured on this event's recap card"
                                >
                                  <Pin className="w-3 h-3" />
                                  Pinned as recap
                                </span>
                              )}
                            </div>
                            {user && user.user_id === event?.creator_id && editingCommentId !== comment.comment_id && (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleTogglePinRecapComment(comment.comment_id)}
                                  className={`p-1.5 rounded-md hover:bg-muted transition-colors ${event?.pinned_recap_comment_id === comment.comment_id ? 'text-amber-600' : 'text-muted-foreground hover:text-foreground'}`}
                                  data-testid={`pin-recap-comment-${comment.comment_id}`}
                                  title={event?.pinned_recap_comment_id === comment.comment_id ? 'Unpin from recap' : 'Pin as recap quote'}
                                >
                                  <Pin className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                            {user && user.user_id === comment.user_id && editingCommentId !== comment.comment_id && (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => { setEditingCommentId(comment.comment_id); setEditCommentText(comment.text); setEditAttachments(Array.isArray(comment.attachments) ? comment.attachments : []); }}
                                  className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors"
                                  data-testid={`edit-comment-${comment.comment_id}`}
                                  title="Edit comment"
                                >
                                  <Edit className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteComment(comment.comment_id)}
                                  className="p-1.5 text-muted-foreground hover:text-destructive rounded-md hover:bg-muted transition-colors"
                                  data-testid={`delete-comment-${comment.comment_id}`}
                                  title="Delete comment"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                          {editingCommentId === comment.comment_id ? (
                            <div>
                              <Textarea
                                value={editCommentText}
                                onChange={(e) => setEditCommentText(e.target.value)}
                                className="rounded-xl border-2 min-h-[80px] mb-2"
                                data-testid="edit-comment-input"
                              />
                              {/* Edit-mode attachment chips */}
                              {editAttachments.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-2" data-testid="edit-comment-attachments">
                                  {editAttachments.map((att, idx) => (
                                    <div key={idx} className="inline-flex items-center gap-2 px-3 py-1.5 bg-muted rounded-full text-sm border border-border">
                                      <FileText className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                                      <span className="max-w-[180px] truncate">{att.filename}</span>
                                      <span className="text-xs text-muted-foreground">{formatFileSize(att.size_bytes)}</span>
                                      <button
                                        type="button"
                                        onClick={() => setEditAttachments((prev) => prev.filter((_, i) => i !== idx))}
                                        className="text-muted-foreground hover:text-destructive"
                                        aria-label={`Remove ${att.filename}`}
                                        data-testid={`remove-edit-attachment-${idx}`}
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <input
                                ref={editAttachmentInputRef}
                                type="file"
                                className="hidden"
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,image/*"
                                multiple
                                onChange={(e) => handleUploadAttachment(e.target.files, 'edit')}
                              />
                              <div className="flex gap-2 flex-wrap">
                                <Button size="sm" onClick={() => handleEditComment(comment.comment_id)} data-testid="save-comment-edit" disabled={uploadingAttachment}>Save</Button>
                                <Button size="sm" variant="outline" onClick={() => { setEditingCommentId(null); setEditCommentText(''); setEditAttachments([]); }} data-testid="cancel-comment-edit">Cancel</Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  type="button"
                                  onClick={() => editAttachmentInputRef.current?.click()}
                                  disabled={uploadingAttachment}
                                  className="flex items-center gap-1.5"
                                >
                                  <Paperclip className="w-3.5 h-3.5" />
                                  {uploadingAttachment ? 'Uploading…' : 'Attach'}
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="text-muted-foreground whitespace-pre-wrap break-words overflow-wrap-anywhere" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}><Linkify text={comment.text} /></p>
                              {/* Attachment cards */}
                              {Array.isArray(comment.attachments) && comment.attachments.length > 0 && (
                                <div className="mt-3 space-y-2" data-testid={`comment-attachments-${comment.comment_id}`}>
                                  {comment.attachments.map((att, idx) => {
                                    const isImage = (att.mime_type || '').startsWith('image/');
                                    return (
                                      <a
                                        key={idx}
                                        href={att.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-3 p-3 bg-muted/50 hover:bg-muted rounded-xl border border-border transition-colors"
                                        data-testid={`comment-attachment-link-${comment.comment_id}-${idx}`}
                                      >
                                        {isImage ? (
                                          <img
                                            src={att.url}
                                            alt={att.filename}
                                            className="w-12 h-12 object-cover rounded-lg border border-border flex-shrink-0"
                                          />
                                        ) : (
                                          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <FileText className="w-5 h-5 text-primary" />
                                          </div>
                                        )}
                                        <div className="min-w-0 flex-1">
                                          <p className="font-medium text-sm truncate text-foreground">{att.filename}</p>
                                          <p className="text-xs text-muted-foreground">
                                            {formatFileSize(att.size_bytes)} · Tap to open
                                          </p>
                                        </div>
                                      </a>
                                    );
                                  })}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {showAuth && <AuthDialog open={showAuth} onClose={() => setShowAuth(false)} />}
      {showMembership && !isIOSApp && <MembershipModal open={showMembership} onClose={() => setShowMembership(false)} />}
      {showReport && event && (
        <ReportDialog
          open={showReport}
          onClose={() => setShowReport(false)}
          contentType="event"
          contentId={event.event_id}
          contentTitle={event.title}
        />
      )}

      {/* Edit Image Dialog */}
      <Dialog
        open={showPromoteDialog}
        onOpenChange={(open) => {
          setShowPromoteDialog(open);
          if (!open) {
            setPromoteNote('');
            setPromoteScope('city');
            setPromoteGroupSlugs([]);
          }
        }}
      >
        <DialogContent className="max-w-md" data-testid="promote-event-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-primary" />
              Promote this event
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {(() => {
              const eventGroups = (event.group_slugs && event.group_slugs.length
                ? event.group_slugs
                : (event.group_slug ? [event.group_slug] : [])).filter(Boolean);
              const hasGroups = eventGroups.length > 0;
              return (
                <>
                  {hasGroups && (
                    <div className="rounded-xl border-2 border-border bg-muted/30 p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold">Audience</div>
                          <div className="text-xs text-muted-foreground">
                            {promoteScope === 'group'
                              ? 'Only members of the selected group(s)'
                              : `All members in ${event.city || 'this city'}`}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-xs">
                          <button
                            type="button"
                            onClick={() => setPromoteScope('city')}
                            className={`px-3 py-1 rounded-full border transition-colors ${promoteScope === 'city' ? 'bg-primary text-white border-primary' : 'bg-white border-border text-muted-foreground hover:bg-muted'}`}
                            data-testid="promote-scope-city"
                          >
                            City-wide
                          </button>
                          <button
                            type="button"
                            onClick={() => setPromoteScope('group')}
                            className={`px-3 py-1 rounded-full border transition-colors ${promoteScope === 'group' ? 'bg-primary text-white border-primary' : 'bg-white border-border text-muted-foreground hover:bg-muted'}`}
                            data-testid="promote-scope-group"
                          >
                            Group only
                          </button>
                        </div>
                      </div>
                      {promoteScope === 'group' && eventGroups.length > 1 && (
                        <div className="mt-3 flex flex-wrap gap-2" data-testid="promote-group-selector">
                          {eventGroups.map((slug) => {
                            const picked = promoteGroupSlugs.includes(slug);
                            return (
                              <button
                                type="button"
                                key={slug}
                                onClick={() =>
                                  setPromoteGroupSlugs((prev) =>
                                    prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
                                  )
                                }
                                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${picked ? 'bg-accent/20 border-accent text-accent-foreground' : 'bg-white border-border text-muted-foreground hover:bg-muted'}`}
                                data-testid={`promote-group-chip-${slug}`}
                              >
                                {picked ? '✓ ' : ''}{slug}
                              </button>
                            );
                          })}
                          <div className="text-[11px] text-muted-foreground w-full">
                            {promoteGroupSlugs.length
                              ? `Promoting to ${promoteGroupSlugs.length} group(s)`
                              : `Leave blank to promote to all ${eventGroups.length} groups`}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-sm text-muted-foreground">
                    {promoteScope === 'group'
                      ? 'Members of the selected group(s) will get a push, in-app, and email notification.'
                      : `Members in ${event.city || 'your area'}${event.country ? `, ${event.country}` : ''} will get a push, in-app, and email notification.`}
                    {' '}Add an optional short note to make it personal.
                  </p>
                  <p className="text-[11px] text-muted-foreground bg-muted/50 rounded-lg px-3 py-2 border border-border/60">
                    ⏱ You can promote weekly as a default. In the final week before the event the cooldown drops to <strong>every 3 days</strong>, and the day before to <strong>every 24 hours</strong>.
                  </p>
                </>
              );
            })()}
            <div className="space-y-2">
              <Label htmlFor="promote-note">
                Your note <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Textarea
                id="promote-note"
                value={promoteNote}
                onChange={(e) => setPromoteNote(e.target.value.slice(0, 500))}
                placeholder={'e.g. "Last 3 spots left! Weather is perfect this weekend \u2600\ufe0f"'}
                rows={4}
                className="resize-none"
                data-testid="promote-note-textarea"
              />
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  Shows in push, in-app and email
                </span>
                <span className={promoteNote.length >= 450 ? 'text-amber-600 font-semibold' : 'text-muted-foreground'}>
                  {promoteNote.length}/500
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <Button
              variant="ghost"
              onClick={() => { setShowPromoteDialog(false); setPromoteNote(''); setPromoteScope('city'); setPromoteGroupSlugs([]); }}
              disabled={promoting}
              data-testid="promote-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePromoteEvent}
              disabled={promoting}
              className="rounded-full font-semibold"
              data-testid="promote-send-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {promoting ? 'Sending\u2026' : 'Send Promotion'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Nudge Attendees Dialog */}
      <Dialog
        open={showNudgeDialog}
        onOpenChange={(open) => {
          setShowNudgeDialog(open);
          if (!open) setNudgeNote('');
        }}
      >
        <DialogContent className="max-w-md" data-testid="nudge-attendees-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-emerald-700" />
              Nudge your attendees
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              A private push + in-app message to people who already RSVP'd. Doesn't count against the public promotion cooldown.
            </p>
            <div className="space-y-2">
              <Label htmlFor="nudge-note">Message</Label>
              <Textarea
                id="nudge-note"
                value={nudgeNote}
                onChange={(e) => setNudgeNote(e.target.value.slice(0, 500))}
                placeholder={'e.g. "Parking fills fast — arrive 15 min early. See you tonight!"'}
                rows={4}
                className="resize-none"
                data-testid="nudge-note-textarea"
              />
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Shows in push + in-app</span>
                <span className={nudgeNote.length >= 450 ? 'text-amber-600 font-semibold' : 'text-muted-foreground'}>
                  {nudgeNote.length}/500
                </span>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={nudgeIncludeWaitlist}
                onChange={(e) => setNudgeIncludeWaitlist(e.target.checked)}
                className="w-4 h-4 rounded"
                data-testid="nudge-include-waitlist"
              />
              <span>Include waitlisted members ({event.waitlist?.length || 0})</span>
            </label>
            <p className="text-[11px] text-muted-foreground bg-muted/50 rounded-lg px-3 py-2 border border-border/60">
              ⏱ Attendee nudges are limited to once per hour so your crowd isn't spammed.
            </p>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <Button
              variant="ghost"
              onClick={() => { setShowNudgeDialog(false); setNudgeNote(''); }}
              disabled={nudging}
              data-testid="nudge-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleNudgeAttendees}
              disabled={nudging || !nudgeNote.trim()}
              className="rounded-full font-semibold"
              data-testid="nudge-send-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {nudging ? 'Sending\u2026' : `Nudge ${(event.attendees?.length || 0) + (nudgeIncludeWaitlist ? (event.waitlist?.length || 0) : 0)}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Download to Share — Photo Picker Dialog */}
      <Dialog
        open={showDownloadDialog}
        onOpenChange={(open) => {
          if (buildingCollage) return;
          setShowDownloadDialog(open);
          if (!open) {
            setSelectedDownloadIds([]);
            setDownloadCaption('');
            setCaptionStyle('quote');
            setIncludeHeader(true);
            setPreviewUrl('');
            setDraggingPhotoId(null);
          }
        }}
      >
        <DialogContent
          className="max-w-2xl max-h-[85vh] overflow-y-auto"
          data-testid="download-share-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-fuchsia-600" />
              Pick photos to share
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span
                className="text-muted-foreground"
                data-testid="download-share-count"
              >
                {selectedDownloadIds.length} of {photos.length} selected
                {selectedDownloadIds.length > 1 && (
                  <span className="ml-2 text-xs">· drag to reorder</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => {
                  if (selectedDownloadIds.length === photos.length) {
                    setSelectedDownloadIds([]);
                  } else {
                    setSelectedDownloadIds(photos.map((p) => p.photo_id));
                  }
                }}
                className="font-semibold text-fuchsia-700 hover:text-fuchsia-900"
                data-testid="download-share-toggle-all"
              >
                {selectedDownloadIds.length === photos.length ? 'Deselect all' : 'Select all'}
              </button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {photos.map((photo) => {
                const orderIdx = selectedDownloadIds.indexOf(photo.photo_id);
                const checked = orderIdx !== -1;
                const isDragging = draggingPhotoId === photo.photo_id;
                return (
                  <div
                    key={photo.photo_id}
                    draggable={checked}
                    onDragStart={(e) => {
                      if (!checked) return;
                      setDraggingPhotoId(photo.photo_id);
                      try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', photo.photo_id); } catch (_) {}
                    }}
                    onDragEnd={() => setDraggingPhotoId(null)}
                    onDragOver={(e) => {
                      if (!checked) return;
                      // Allow drop even if local state hasn't propagated — the
                      // dragged id is also carried in dataTransfer.
                      e.preventDefault();
                      try { e.dataTransfer.dropEffect = 'move'; } catch (_) {}
                    }}
                    onDrop={(e) => {
                      if (!checked) return;
                      e.preventDefault();
                      let fromId = draggingPhotoId;
                      try {
                        const dtId = e.dataTransfer.getData('text/plain');
                        if (dtId) fromId = dtId;
                      } catch (_) {}
                      if (!fromId || fromId === photo.photo_id) {
                        setDraggingPhotoId(null);
                        return;
                      }
                      setSelectedDownloadIds((prev) => {
                        const from = prev.indexOf(fromId);
                        const to = prev.indexOf(photo.photo_id);
                        if (from === -1 || to === -1) return prev;
                        const next = prev.slice();
                        next.splice(from, 1);
                        next.splice(to, 0, fromId);
                        return next;
                      });
                      setDraggingPhotoId(null);
                    }}
                    className={isDragging ? 'opacity-50' : ''}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedDownloadIds((prev) =>
                          prev.includes(photo.photo_id)
                            ? prev.filter((id) => id !== photo.photo_id)
                            : [...prev, photo.photo_id]
                        );
                      }}
                      className={`relative w-full aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        checked
                          ? 'border-fuchsia-600 ring-2 ring-fuchsia-300 cursor-grab active:cursor-grabbing'
                          : 'border-transparent hover:border-fuchsia-200'
                      }`}
                      data-testid={`download-share-photo-${photo.photo_id}`}
                      aria-pressed={checked}
                    >
                      <img
                        src={photo.image_data || photo.image_url}
                        alt={photo.caption || 'Event photo'}
                        className={`w-full h-full object-cover transition-opacity ${checked ? '' : 'opacity-80'}`}
                        loading="lazy"
                        draggable={false}
                      />
                      {checked ? (
                        <div
                          className="absolute top-1.5 right-1.5 min-w-[1.5rem] h-6 px-1.5 rounded-full flex items-center justify-center text-xs font-bold shadow bg-fuchsia-600 text-white"
                          data-testid={`download-share-order-${photo.photo_id}`}
                        >
                          {orderIdx + 1}
                        </div>
                      ) : (
                        <div className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shadow bg-white/90 text-muted-foreground border border-border" />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Include header toggle */}
            <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/40 border">
              <div className="min-w-0">
                <div className="text-sm font-medium">Include header box</div>
                <div className="text-xs text-muted-foreground">
                  Title, headcount, date & caption on the purple band.
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer select-none shrink-0 ml-3">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={includeHeader}
                  onChange={(e) => setIncludeHeader(e.target.checked)}
                  data-testid="download-share-include-header"
                />
                <div className="w-11 h-6 bg-muted rounded-full peer-checked:bg-fuchsia-600 transition-colors" />
                <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
              </label>
            </div>

            {/* Caption / Quote - only when header is included */}
            {includeHeader ? (
              <div className="space-y-1.5">
              <Label htmlFor="download-share-caption" className="text-sm font-medium">
                Add a caption or quote (optional)
              </Label>
              <Textarea
                id="download-share-caption"
                value={downloadCaption}
                onChange={(e) => setDownloadCaption(e.target.value.slice(0, 140))}
                placeholder="e.g. One unforgettable evening with the crew ✨"
                maxLength={140}
                rows={2}
                className="resize-none"
                data-testid="download-share-caption"
              />
              <div className="flex items-center justify-between gap-2">
                <div className="flex gap-1.5 flex-wrap">
                  {[
                    { key: 'quote', label: 'Quote' },
                    { key: 'handwritten', label: 'Handwritten' },
                    { key: 'headline', label: 'Headline' },
                  ].map((opt) => {
                    const active = captionStyle === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setCaptionStyle(opt.key)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                          active
                            ? 'bg-fuchsia-600 border-fuchsia-600 text-white'
                            : 'bg-white border-border text-muted-foreground hover:border-fuchsia-300'
                        } ${opt.key === 'handwritten' ? "[font-family:'Caveat',cursive] !text-base" : ''} ${opt.key === 'headline' ? 'uppercase tracking-wider' : ''} ${opt.key === 'quote' ? 'italic' : ''}`}
                        data-testid={`download-share-style-${opt.key}`}
                        aria-pressed={active}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <div className="text-xs text-muted-foreground whitespace-nowrap" data-testid="download-share-caption-counter">
                  {downloadCaption.length}/140
                </div>
              </div>
              </div>
            ) : null}

            {/* Live preview */}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Preview</Label>
              <div
                className="relative rounded-xl border bg-muted/40 flex items-center justify-center overflow-hidden"
                style={{ minHeight: 220 }}
                data-testid="download-share-preview"
              >
                {selectedDownloadIds.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-10 px-6 text-center">
                    Pick at least one photo to see the preview
                  </div>
                ) : previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Collage preview"
                    className={`max-h-72 w-auto object-contain transition-opacity ${previewLoading ? 'opacity-60' : 'opacity-100'}`}
                    data-testid="download-share-preview-img"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 py-10 text-muted-foreground text-sm">
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Building preview…
                  </div>
                )}
                {previewLoading && previewUrl && (
                  <div className="absolute top-2 right-2 bg-white/90 rounded-full p-1 shadow" data-testid="download-share-preview-spinner">
                    <RefreshCw className="w-4 h-4 animate-spin text-fuchsia-600" />
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t">
              <Button
                variant="ghost"
                onClick={() => {
                  if (buildingCollage) return;
                  setShowDownloadDialog(false);
                  setSelectedDownloadIds([]);
                  setDownloadCaption('');
                  setCaptionStyle('quote');
                  setIncludeHeader(true);
                  setPreviewUrl('');
                }}
                disabled={buildingCollage}
                className="rounded-full"
                data-testid="download-share-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (selectedDownloadIds.length === 0) return;
                  const backend = process.env.REACT_APP_BACKEND_URL;
                  const idOrder = selectedDownloadIds;
                  const photoById = Object.fromEntries(photos.map((p) => [p.photo_id, p]));
                  const items = idOrder
                    .map((id) => photoById[id])
                    .filter(Boolean)
                    .map((p) => {
                      let src = p.image_url || p.image_data;
                      if (src && src.startsWith('/api/')) src = `${backend}${src}`;
                      return { url: src, caption: p.caption };
                    })
                    .filter((p) => p.url);
                  if (items.length === 0) {
                    toast.error('Those photos could not be loaded');
                    return;
                  }
                  setBuildingCollage(true);
                  toast.loading('Building your shareable card…', { id: 'collage' });
                  try {
                    const dateLabel = event.event_date
                      ? new Date(event.event_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                      : undefined;
                    const { blob } = await buildAlbumCollage(items, {
                      title: event.title,
                      attendeeCount: calcHeadcount(event),
                      dateLabel,
                      caption: downloadCaption,
                      captionStyle,
                      includeHeader,
                    });
                    toast.dismiss('collage');
                    const filename = `${event.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-album.jpg`;
                    downloadBlob({ blob, filename });

                    // After the file is saved, offer a one-tap native share
                    // so the user can hand it straight to Instagram /
                    // WhatsApp without re-attaching. Guarded by
                    // navigator.canShare({ files }) for desktop/older browsers.
                    let canShareFile = false;
                    try {
                      const probeFile = new File([blob], filename, { type: blob.type || 'image/jpeg' });
                      canShareFile = !!(navigator.canShare && navigator.canShare({ files: [probeFile] }));
                    } catch (_) {
                      canShareFile = false;
                    }
                    toast.success('Album saved to your device', {
                      description: canShareFile
                        ? 'Tap Share now to send it to any app, or open your gallery to attach it later.'
                        : 'Open Instagram, WhatsApp, or any app and attach the image you just saved.',
                      action: canShareFile
                        ? {
                            label: 'Share now',
                            onClick: async () => {
                              try {
                                const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
                                await navigator.share({
                                  files: [file],
                                  title: event.title,
                                  text: downloadCaption || `${event.title} — shared from ExpatsSphere`,
                                });
                              } catch (_) {
                                // User cancelled or share failed — stay silent.
                              }
                            },
                          }
                        : undefined,
                    });
                    setShowDownloadDialog(false);
                    setSelectedDownloadIds([]);
                    setDownloadCaption('');
                    setCaptionStyle('quote');
                    setIncludeHeader(true);
                    setPreviewUrl('');
                  } catch (e) {
                    toast.dismiss('collage');
                    toast.error('Could not build the album card');
                  } finally {
                    setBuildingCollage(false);
                  }
                }}
                disabled={selectedDownloadIds.length === 0 || buildingCollage}
                className="rounded-full font-semibold bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                data-testid="download-share-generate"
              >
                <Download className="w-4 h-4 mr-2" />
                {buildingCollage
                  ? 'Building…'
                  : selectedDownloadIds.length > 0
                    ? `Download this (${selectedDownloadIds.length})`
                    : 'Download'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Attendee Report-Photo Dialog */}
      <Dialog
        open={!!photoToReport}
        onOpenChange={(open) => {
          if (reportBusy) return;
          if (!open) setPhotoToReport(null);
        }}
      >
        <DialogContent className="max-w-sm" data-testid="report-photo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-yellow-600" />
              Report this photo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {photoToReport && (
              <div className="rounded-lg overflow-hidden border bg-muted/40">
                <img
                  src={photoToReport.image_data || photoToReport.image_url}
                  alt={photoToReport.caption || 'Reported photo'}
                  className="w-full max-h-40 object-contain bg-black/5"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Reason</Label>
              <div className="space-y-1.5" role="radiogroup" data-testid="report-reason-group">
                {[
                  { key: 'inappropriate', label: 'Inappropriate / explicit content' },
                  { key: 'not_from_event', label: 'Not from this event' },
                  { key: 'harassment', label: 'Harassment / targets a person' },
                  { key: 'spam', label: 'Spam / off-topic' },
                  { key: 'other', label: 'Other' },
                ].map((opt) => (
                  <label
                    key={opt.key}
                    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm ${
                      reportReason === opt.key
                        ? 'bg-yellow-50 border-yellow-400'
                        : 'bg-white border-border hover:border-yellow-200'
                    }`}
                  >
                    <input
                      type="radio"
                      name="report-reason"
                      value={opt.key}
                      checked={reportReason === opt.key}
                      onChange={() => setReportReason(opt.key)}
                      className="accent-yellow-500"
                      data-testid={`report-reason-${opt.key}`}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="report-note" className="text-sm font-medium">Add context (optional)</Label>
              <Textarea
                id="report-note"
                value={reportNote}
                onChange={(e) => setReportNote(e.target.value.slice(0, 280))}
                placeholder="Anything the host should know?"
                rows={2}
                maxLength={280}
                className="resize-none"
                data-testid="report-note-input"
              />
              <div className="text-xs text-muted-foreground text-right">{reportNote.length}/280</div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t">
              <Button
                variant="ghost"
                onClick={() => setPhotoToReport(null)}
                disabled={reportBusy}
                className="rounded-full"
                data-testid="report-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={submitPhotoReport}
                disabled={reportBusy}
                className="rounded-full bg-yellow-600 hover:bg-yellow-700 text-white"
                data-testid="report-submit"
              >
                {reportBusy ? 'Submitting…' : 'Submit report'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Delete-Photo Confirm Dialog — moderation UX for hosts/admins */}
      <Dialog
        open={!!photoToDelete}
        onOpenChange={(open) => {
          if (deletingPhoto) return;
          if (!open) setPhotoToDelete(null);
        }}
      >
        <DialogContent className="max-w-sm" data-testid="delete-photo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-destructive" />
              Delete this photo?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {photoToDelete && (
              <div className="rounded-lg overflow-hidden border bg-muted/40">
                <img
                  src={photoToDelete.image_data || photoToDelete.image_url}
                  alt={photoToDelete.caption || 'Event photo'}
                  className="w-full max-h-48 object-contain bg-black/5"
                />
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              {photoToDelete && photoToDelete.uploader_id !== user?.user_id ? (
                <>Uploaded by <strong>{photoToDelete.uploader_name || 'another attendee'}</strong>. The uploader will not be notified.</>
              ) : (
                <>This can't be undone.</>
              )}
            </p>
            <div className="flex items-center justify-end gap-2 pt-2 border-t">
              <Button
                variant="ghost"
                onClick={() => setPhotoToDelete(null)}
                disabled={deletingPhoto}
                className="rounded-full"
                data-testid="delete-photo-cancel"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeletePhoto}
                disabled={deletingPhoto}
                className="rounded-full"
                data-testid="delete-photo-confirm"
              >
                {deletingPhoto ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Delete / Rescue Dialog — host picks "Just cancel" or "Reschedule & Invite" */}
      <Dialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          if (cancelBusy || rebookBusy) return;
          setShowDeleteDialog(open);
        }}
      >
        <DialogContent className="max-w-md" data-testid="delete-rescue-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-destructive" />
              Cancel this event?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Everyone who RSVP'd will be notified. Want to <strong>reschedule instead</strong>? Fill in a new date below and we'll invite them to the new one — no cancel email, just the rebook.
            </p>

            <div className="rounded-xl border bg-muted/40 p-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="rebook-date" className="text-xs font-medium">New date</Label>
                  <Input
                    id="rebook-date"
                    type="date"
                    value={rebookDate}
                    onChange={(e) => setRebookDate(e.target.value)}
                    data-testid="rebook-date-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="rebook-time" className="text-xs font-medium">New time</Label>
                  <Input
                    id="rebook-time"
                    type="time"
                    value={rebookTime}
                    onChange={(e) => setRebookTime(e.target.value)}
                    data-testid="rebook-time-input"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="rebook-note" className="text-xs font-medium">
                  Optional note to previous RSVPs
                </Label>
                <Textarea
                  id="rebook-note"
                  value={rebookNote}
                  onChange={(e) => setRebookNote(e.target.value.slice(0, 140))}
                  placeholder="Sorry for the shuffle — here's the new plan 🙏"
                  rows={2}
                  maxLength={140}
                  className="resize-none"
                  data-testid="rebook-note-input"
                />
                <div className="text-xs text-muted-foreground text-right">{rebookNote.length}/140</div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 pt-2 border-t">
              <Button
                variant="destructive"
                onClick={handlePlainCancel}
                disabled={cancelBusy || rebookBusy}
                className="rounded-full"
                data-testid="plain-cancel-btn"
              >
                {cancelBusy ? 'Cancelling…' : 'Just cancel'}
              </Button>
              <Button
                onClick={handleRebook}
                disabled={!rebookDate || !rebookTime || cancelBusy || rebookBusy}
                className="rounded-full font-semibold bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                data-testid="rebook-submit-btn"
              >
                {rebookBusy ? 'Rebooking…' : 'Reschedule & Invite'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Promotion History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="promotion-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-primary" />
              Promotion History
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            {promotionHistory.length === 0 ? (
              <p className="text-sm text-muted-foreground">No promotions sent yet.</p>
            ) : (
              promotionHistory.map((p) => {
                const date = p.created_at ? new Date(p.created_at) : null;
                const dateLabel = date ? date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '';
                const conversion = p.recipient_count ? Math.round((p.rsvps_count / p.recipient_count) * 1000) / 10 : 0;
                return (
                  <div
                    key={p.promotion_id}
                    className="rounded-xl border-2 border-border bg-white p-4 space-y-3"
                    data-testid={`promotion-history-item-${p.promotion_id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-xs text-muted-foreground">{dateLabel}</div>
                        <div className="text-xs font-medium mt-0.5">
                          {p.scope === 'group'
                            ? `Group only${(p.target_group_slugs || []).length ? ` · ${p.target_group_slugs.join(', ')}` : ''}`
                            : 'City-wide'}
                        </div>
                      </div>
                      <div className="text-xs font-bold text-primary shrink-0">
                        {conversion}% RSVP rate
                      </div>
                    </div>
                    {p.note && (
                      <div className="rounded-lg bg-primary/5 border-l-4 border-primary px-3 py-2 italic text-sm">
                        &ldquo;{p.note}&rdquo;
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="rounded-lg bg-muted/50 py-2">
                        <div className="text-lg font-bold">{p.recipient_count}</div>
                        <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Reached</div>
                      </div>
                      <div className="rounded-lg bg-sky-50 py-2">
                        <div className="text-lg font-bold text-sky-700">{p.views_count}</div>
                        <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Opened</div>
                      </div>
                      <div className="rounded-lg bg-emerald-50 py-2">
                        <div className="text-lg font-bold text-emerald-700">{p.rsvps_count}</div>
                        <div className="text-[11px] text-muted-foreground uppercase tracking-wide">RSVP'd</div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditImage} onOpenChange={setShowEditImage}>  <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Event Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Current Image Preview */}
            <div className="space-y-2">
              <Label>Current Image</Label>
              <div className="border rounded-lg overflow-hidden h-40 bg-muted">
                {event?.image_url ? (
                  <img 
                    src={event.image_url} 
                    alt="Current"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    No image
                  </div>
                )}
              </div>
            </div>

            {/* Upload New Image */}
            <div className="space-y-2">
              <Label>Upload New Image</Label>
              {newImageFile ? (
                <div className="relative">
                  <img 
                    src={newImageFile} 
                    alt="New preview"
                    className="w-full h-40 object-cover rounded-lg border"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2 rounded-full"
                    onClick={() => {
                      setNewImageFile(null);
                      if (editImageInputRef.current) editImageInputRef.current.value = '';
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div 
                  className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => editImageInputRef.current?.click()}
                >
                  <Upload className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm font-medium">Click to upload</p>
                  <p className="text-xs text-muted-foreground">Max 2MB</p>
                </div>
              )}
              <input
                ref={editImageInputRef}
                type="file"
                accept={isIOSApp ? ".jpg,.jpeg,.png,.gif,.webp,.heic" : "image/*"}
                onChange={handleEditImageFileChange}
                className="hidden"
              />
            </div>

            {/* Or Use URL */}
            {!newImageFile && (
              <div className="space-y-2">
                <Label>Or Enter Image URL</Label>
                <Input
                  type="url"
                  placeholder="https://images.unsplash.com/..."
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowEditImage(false);
                setNewImageFile(null);
                setNewImageUrl('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEventImage}
              disabled={savingImage || (!newImageUrl && !newImageFile)}
            >
              {savingImage ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Save Image
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Host: adjust an attendee's guest count */}
      <Dialog open={!!hostGuestEditor} onOpenChange={(open) => { if (!open) setHostGuestEditor(null); }}>
        <DialogContent className="max-w-sm" data-testid="host-guest-editor-dialog">
          <DialogHeader>
            <DialogTitle>Set guest count for {hostGuestEditor?.name}</DialogTitle>
          </DialogHeader>
          {hostGuestEditor && (
            <HostGuestStepper
              editor={hostGuestEditor}
              maxGuests={event?.max_guests_per_rsvp}
              saving={savingHostGuest}
              onCancel={() => setHostGuestEditor(null)}
              onSave={(next) => handleHostSaveAttendeeGuests(hostGuestEditor.user_id, next)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const HostGuestStepper = ({ editor, maxGuests, saving, onCancel, onSave }) => {
  const [value, setValue] = useState(editor.current || 0);
  const capped = maxGuests != null ? maxGuests : 99;
  return (
    <div className="space-y-4 py-2" data-testid="host-guest-stepper">
      <div className="flex items-center justify-center gap-3 bg-sky-50 border-2 border-sky-200 rounded-full px-4 py-3">
        <button
          type="button"
          onClick={() => setValue((v) => Math.max(0, v - 1))}
          disabled={saving || value <= 0}
          className="w-8 h-8 rounded-full bg-white border border-sky-300 text-sky-700 font-bold disabled:opacity-40"
          data-testid="host-guest-decrement"
        >
          −
        </button>
        <span className="text-lg font-bold text-sky-900 min-w-[2rem] text-center" data-testid="host-guest-count">
          {value}
        </span>
        <button
          type="button"
          onClick={() => setValue((v) => Math.min(capped, v + 1))}
          disabled={saving || value >= capped}
          className="w-8 h-8 rounded-full bg-sky-600 text-white font-bold disabled:opacity-40"
          data-testid="host-guest-increment"
        >
          +
        </button>
        <span className="text-xs text-sky-700 ml-1">
          {maxGuests != null ? `guest${maxGuests !== 1 ? 's' : ''} (max ${maxGuests})` : 'guests'}
        </span>
      </div>
      <p className="text-xs text-muted-foreground text-center">
        {editor.name} will be notified of this change.
      </p>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onCancel} disabled={saving} className="flex-1" data-testid="host-guest-cancel">
          Cancel
        </Button>
        <Button
          onClick={() => onSave(value)}
          disabled={saving || value === editor.current}
          className="flex-1 bg-sky-600 hover:bg-sky-700 text-white"
          data-testid="host-guest-save"
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

export default EventDetail;